import React, { useState, useEffect, useRef } from 'react'
import { trackFathomEvent } from '../../helpers/fathom'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import { auth, db, storage, functions } from '../../helpers/firebase'
import { 
  collection, 
  query, 
  where, 
  getDocs,
  orderBy,
  doc,
  getDoc,
  addDoc,
  deleteDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { httpsCallable } from 'firebase/functions'
import { ParentInviteLinkForm, prefetchParentInvitePreview } from './ParentInviteClaim'
import { clearParentInviteToken, readParentInviteToken } from '../../helpers/parentInviteStorage'
import { useHistory } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import SortIcon from '@material-ui/icons/Sort'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import BlockIcon from '@material-ui/icons/Block'
import Brightness4Icon from '@material-ui/icons/Brightness4'
import Brightness7Icon from '@material-ui/icons/Brightness7'
import PeopleIcon from '@material-ui/icons/People'
import SettingsIcon from '@material-ui/icons/Settings'
import AddIcon from '@material-ui/icons/Add'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import MixedContentPreview from '../UiElements/MixedContentPreview'
import BetaVersionBadge from '../UiElements/BetaVersionBadge'
import QuestionDetailModal from './QuestionDetailModal'
import ImageCrop from '../UiElements/ImageCrop'
import { processScreenshotWithMathpix } from '../../helpers/screenshotProcessor'
import ParentTutorial from './ParentTutorial'
import ParentChecklist from './ParentChecklist'
import ParentNameModal from './ParentNameModal'
import OnboardingChoiceModal from './OnboardingChoiceModal'
import StudyHelperDemo from './StudyHelperDemo'
import ParentPinModal from './ParentPinModal'
import StudyRoom from './StudyRoom'
import ParentExplainPanel from './ParentExplainPanel'
import CompetitionEnrollModal, { COMPETITION_END } from './CompetitionEnrollModal'
import ParentTierModal from './ParentTierModal'
import ManageTiers, { BillingSummaryNotice } from './ManageTiers'
import {
  checkoutReturnShouldRefresh,
  searchWithoutBillingReturn,
  tierAccessLabel,
  tierActionLabel
} from './parentBillingDisplay'
import { ROLE_PARENT, isStudentRole } from '../../constants/roles'
import './ParentDashboard.css'
import '../../styles/dynamic-background.css'

// Auth address given to students who sign in with a username. It is not a
// deliverable inbox, so it is never shown to a parent.
const PLACEHOLDER_EMAIL_DOMAIN = '@parentstudent.quillnote.app'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Credential-light students (Phase 2) have no Firebase Auth user and no
// email or username at all. Profiles created before this field existed always
// have a login, hence the !== false rather than a truthiness check.
const hasStudentLogin = (student) => student?.hasLoginCredentials !== false

// Upload and delete must derive the same object name from the same page number,
// or deleting a page leaves its image orphaned in the bucket forever. The
// leading parentUid segment is also what storage.rules checks for ownership.
const pageImagePath = (parentUid, studentId, pageNumber) =>
  `pages/${parentUid}/${studentId}/page_${pageNumber}.jpg`

// What to show under a student's name: their real email, or the username they
// sign in with, or a plain-language note for a student who has no login yet.
const studentLoginLabel = (student) => {
  if (!hasStudentLogin(student)) return 'Signs in from your account'
  const email = student?.email || ''
  if (!email || email.endsWith(PLACEHOLDER_EMAIL_DOMAIN)) {
    return student?.username || 'Signs in from your account'
  }
  return email
}

const studentRosterName = (student) => {
  const first = (student?.firstName || '').trim()
  const last = (student?.lastName || '').trim()
  if (first) return last ? `${first} ${last}` : first
  return (student?.username || '').trim() || 'Student'
}

const ThemeToggleButton = () => {
  const { theme, toggleTheme } = useTheme()
  return (
    <button
      className='qn-theme-toggle'
      onClick={toggleTheme}
      aria-label='Toggle theme'
    >
      {theme === 'dark' ? <Brightness7Icon style={{ fontSize: 20 }} /> : <Brightness4Icon style={{ fontSize: 20 }} />}
    </button>
  )
}

const ParentDashboard = () => {
  const {
    currentUser,
    userProfile,
    logout,
    createStudentAccount,
    linkStudentCredentials,
    sendStudentPasswordReset,
    refreshUserProfile,
    completeOnboardingChoice,
    setStudentViewActive,
    setStudentViewActingStudentId,
    setParentTaskModalActive,
    startStudentViewSession,
    endStudentViewSession,
    setParentPin,
    changeParentPin,
    removeParentPin,
    requestParentPinReset,
    confirmParentPinReset
  } = useAuth()
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()
  const [students, setStudents] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [studyQuestions, setStudyQuestions] = useState([])
  const [loadingQuestions, setLoadingQuestions] = useState(false)
  const [showPhoneModal, setShowPhoneModal] = useState(false)
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [savingPhone, setSavingPhone] = useState(false)
  const [smsConsent, setSmsConsent] = useState(false)
  const [selectedQuestion, setSelectedQuestion] = useState(null)
  const [showCreateStudentModal, setShowCreateStudentModal] = useState(false)
  const [enrollInCompetition, setEnrollInCompetition] = useState(true)
  const [studentFormData, setStudentFormData] = useState({
    firstName: '',
    username: '',
    password: '',
    email: ''
  })
  // Add Student: "This student will only use Quillnote from my account."
  // Off by default — the credential path stays the default experience.
  const [studentViewOnly, setStudentViewOnly] = useState(false)
  const [creatingStudent, setCreatingStudent] = useState(false)
  const [showPasswordInModal, setShowPasswordInModal] = useState(false)
  // "Add a login later" form on a credential-light student's Settings tab.
  const [linkFormData, setLinkFormData] = useState({
    username: '',
    password: '',
    email: ''
  })
  const [linkUseParentEmail, setLinkUseParentEmail] = useState(false)
  const [showLinkPassword, setShowLinkPassword] = useState(false)
  const [linkingCredentials, setLinkingCredentials] = useState(false)
  const [showManageStudentDropdown, setShowManageStudentDropdown] = useState(false)
  const [showSortDropdown, setShowSortDropdown] = useState(false)
  const [showSortOrderDropdown, setShowSortOrderDropdown] = useState(false)
  const [sortBy, setSortBy] = useState('date')
  const [sortOrder, setSortOrder] = useState('desc')
  const [aiChatHelperEnabled, setAiChatHelperEnabled] = useState(true)
  const [reportsEnabled, setReportsEnabled] = useState(true)
  const [reportScreencapEnabled, setReportScreencapEnabled] = useState(true)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [showMobileMoreMenu, setShowMobileMoreMenu] = useState(false)
  const [selectedMenuItem, setSelectedMenuItem] = useState('my-students')
  const [selectedStudentMenuItem, setSelectedStudentMenuItem] = useState('view-work')
  const [selectedSettingsTab, setSelectedSettingsTab] = useState('notifications')
  const [selectedViewWorkQuestion, setSelectedViewWorkQuestion] = useState(null)
  const [showViewWorkSortDropdown, setShowViewWorkSortDropdown] = useState(false)
  const [desktopVideoRecommendations, setDesktopVideoRecommendations] = useState([])
  const [showChatConversationModal, setShowChatConversationModal] = useState(false)
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false)
  const [useParentEmail, setUseParentEmail] = useState(false)

  const [showTutorial, setShowTutorial] = useState(false)
  const [showChecklist, setShowChecklist] = useState(false)
  const [checklistAutoOpen, setChecklistAutoOpen] = useState(false)
  // Whether the checklist's popover panel (not just its always-present FAB)
  // is currently expanded. Reported by ParentChecklist itself — see the
  // parentTaskModalActive effect below, which folds this in so a full-screen
  // surface (competition enrollment, release notes) can never render behind
  // the panel the instant it auto-opens after the legacy tutorial finishes.
  const [checklistPanelOpen, setChecklistPanelOpen] = useState(false)
  const onboardingDetermined = useRef(false)

  // Phase 3: post-name "Add a student / See a demo" choice, and the demo
  // shell itself. resolvingOnboardingChoice guards against a double-tap
  // firing resolveOnboardingChoice twice while the callable is in flight.
  const [resolvingOnboardingChoice, setResolvingOnboardingChoice] = useState(false)
  const [showDemo, setShowDemo] = useState(false)

  // Phase 4: Student View and the parent PIN. studentViewStudentId is only ever
  // a mirror of the authoritative session document the server owns — the
  // dashboard reads it back on mount so a page refresh cannot be used to
  // escape the lock, and every state change here is the result of a callable.
  const [studentViewStudentId, setStudentViewStudentId] = useState(null)
  const [studentViewPinProtected, setStudentViewPinProtected] = useState(false)
  const [enteringStudentView, setEnteringStudentView] = useState(false)
  const [leavingStudentView, setLeavingStudentView] = useState(false)
  const [showPinOffer, setShowPinOffer] = useState(false)
  const [showLeaveStudentViewConfirm, setShowLeaveStudentViewConfirm] = useState(false)
  // null | 'set' | 'change' | 'remove' | 'verify' | 'reset'
  const [pinModalMode, setPinModalMode] = useState(null)
  const [pinBusy, setPinBusy] = useState(false)
  const [pinError, setPinError] = useState(null)
  const [pinNotice, setPinNotice] = useState(null)
  const [sendingResetCode, setSendingResetCode] = useState(false)
  // Blocks the parent shell until we know whether this browser is resuming a
  // Student View session, so a reload never flashes the parent's navigation in
  // front of a child.
  const [studentViewRestorePending, setStudentViewRestorePending] = useState(true)
  const studentViewRestoreAttempted = useRef(false)

  const [showCompetitionModal, setShowCompetitionModal] = useState(false)
  const [competitionEnrolledIds, setCompetitionEnrolledIds] = useState(new Set())
  const competitionModalReady = useRef(false)
  // Rows from getParentBillingSummary, keyed by student id. A missing row is
  // not a tier: the card stays unlabeled rather than guessing from parentId.
  const [billingById, setBillingById] = useState({})
  const [billingSummaryError, setBillingSummaryError] = useState(false)
  const [tierTarget, setTierTarget] = useState(null)
  const billingRequestId = useRef(0)
  const billingHasSummary = useRef(false)
  const loadBillingSummaryRef = useRef(async () => {})
  const billingReturnScheduled = useRef(false)
  const billingReturnTimer = useRef(null)
  const dashboardMounted = useRef(true)

  const [emailWelcomeEnabled, setEmailWelcomeEnabled] = useState(true)
  const [emailDailySummaryEnabled, setEmailDailySummaryEnabled] = useState(true)
  const [emailWeeklyDigestEnabled, setEmailWeeklyDigestEnabled] = useState(true)
  const [emailVersionUpdatesEnabled, setEmailVersionUpdatesEnabled] = useState(true)
  const [savingEmailPrefs, setSavingEmailPrefs] = useState(false)

  const [pages, setPages] = useState([])
  const [loadingPages, setLoadingPages] = useState(false)
  const [selectedPage, setSelectedPage] = useState(null)
  const [confirmDeletePageId, setConfirmDeletePageId] = useState(null)
  const [pageNumber, setPageNumber] = useState('')
  const [showPageNumberModal, setShowPageNumberModal] = useState(false)
  const [pageImageSrc, setPageImageSrc] = useState(null)
  const [showPageImageCrop, setShowPageImageCrop] = useState(false)
  const [processingPage, setProcessingPage] = useState(false)
  const [pageCreationProgress, setPageCreationProgress] = useState(0)
  const [pageCreationStep, setPageCreationStep] = useState('')
  const pageImageFileRef = useRef(null)

  useEffect(() => {
    if (userProfile && userProfile.role !== ROLE_PARENT) {
      enqueueSnackbar({
        message: 'Access denied. Parent privileges required.',
        variant: 'error'
      })
      history.replace('/')
    } else if (currentUser) {
      loadLinkedStudents()
      const prefs = userProfile?.emailNotifications || {}
      setEmailWelcomeEnabled(prefs.welcomeEmail !== false)
      setEmailDailySummaryEnabled(prefs.dailySummary !== false)
      setEmailWeeklyDigestEnabled(prefs.weeklyDigest !== false)
      setEmailVersionUpdatesEnabled(prefs.versionUpdates !== false)
    }
  }, [userProfile, currentUser, history])

  const isParent = userProfile?.role === ROLE_PARENT
  // New parent accounts only (Phase 1) — provisionParentProfile sets this,
  // legacy parents never have it, so they are never prompted for a name.
  const showNameModal = isParent && userProfile?.pendingParentName === true
  const [inviteClaimToken, setInviteClaimToken] = useState(readParentInviteToken)
  const [settingsFirstName, setSettingsFirstName] = useState('')
  const [savingSettingsFirstName, setSavingSettingsFirstName] = useState(false)
  const showInviteClaim = isParent && !showNameModal && !!inviteClaimToken

  // Start the invite read while the parent is still entering their name, so
  // the link screen is ready the moment that modal closes.
  useEffect(() => {
    if (inviteClaimToken) prefetchParentInvitePreview(inviteClaimToken)
  }, [inviteClaimToken])
  // Phase 3: set by completeParentName right after the name modal succeeds,
  // so it only ever applies to accounts that went through the new flow.
  // Legacy parents never have this field either, so nothing changes for them.
  const onboardingChoicePending = isParent && userProfile?.pendingOnboardingChoice === true
  const showOnboardingChoice = onboardingChoicePending && !showNameModal && !showInviteClaim && !showCreateStudentModal && !showDemo

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    dashboardMounted.current = true
    return () => {
      dashboardMounted.current = false
      if (billingReturnTimer.current) {
        window.clearTimeout(billingReturnTimer.current)
      }
    }
  }, [])

  // Checkout and the Customer Portal send the parent back here. The query is
  // removed and is never copied onto a card. A second summary read only
  // happens so a webhook that lands a moment later can update the label.
  useEffect(() => {
    if (!currentUser || !userProfile || userProfile.role !== ROLE_PARENT) return undefined
    const search = window.location.search
    const nextSearch = searchWithoutBillingReturn(search)
    if (nextSearch !== search) {
      history.replace({
        pathname: '/parent/dashboard',
        search: nextSearch
      })
    }
    if (!checkoutReturnShouldRefresh(search, document.referrer)) return undefined
    if (billingReturnScheduled.current) return undefined
    billingReturnScheduled.current = true
    billingReturnTimer.current = window.setTimeout(() => {
      loadBillingSummaryRef.current()
    }, 2000)
    return undefined
  }, [currentUser, userProfile, history])

  useEffect(() => {
    if (!showTutorial && competitionModalReady.current) {
      competitionModalReady.current = false
      setTimeout(() => setShowCompetitionModal(true), 600)
    }
  }, [showTutorial])

  const loadBillingSummary = async () => {
    const requestId = billingRequestId.current + 1
    billingRequestId.current = requestId
    try {
      const summarize = httpsCallable(functions, 'getParentBillingSummary')
      const result = await summarize({})
      if (!dashboardMounted.current || requestId !== billingRequestId.current) return
      const payload = result && result.data && result.data.students
      if (!Array.isArray(payload)) {
        throw new Error('Billing summary was incomplete')
      }
      const next = {}
      payload.forEach((row) => {
        if (!row || typeof row.studentId !== 'string' || !row.studentId) return
        if (!tierAccessLabel(row.access)) return
        next[row.studentId] = {
          studentId: row.studentId,
          access: row.access,
          renewalAt: Number.isFinite(row.renewalAt) ? row.renewalAt : null,
          cancelAtPeriodEnd: row.cancelAtPeriodEnd === true
        }
      })
      billingHasSummary.current = true
      setBillingById(next)
      setBillingSummaryError(false)
    } catch (error) {
      if (!dashboardMounted.current || requestId !== billingRequestId.current) return
      console.error('Error loading billing summary:', error)
      if (billingHasSummary.current) return
      setBillingById({})
      setBillingSummaryError(true)
    }
  }
  loadBillingSummaryRef.current = loadBillingSummary

  // Get all students linked to this parent.
  const fetchLinkedStudents = async () => {
    const usersRef = collection(db, 'users')
    const q = query(usersRef, where('parentId', '==', currentUser.uid))
    const studentsSnapshot = await getDocs(q)

    return studentsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  }

  // Re-reads the roster after a profile changes (new student, login added).
  // Deliberately does not re-run the onboarding and competition decisions in
  // loadLinkedStudents: those are made once per session, and re-opening their
  // modals in the middle of a task would be wrong.
  const refreshStudents = async (selectStudentId = null) => {
    try {
      const linkedStudents = await fetchLinkedStudents()
      setStudents(linkedStudents)
      setSelectedStudent(prev => {
        const targetId = selectStudentId || prev?.id
        if (!targetId) return prev
        return linkedStudents.find(s => s.id === targetId) || prev
      })
      loadBillingSummary()
      return linkedStudents
    } catch (error) {
      console.error('Error refreshing students:', error)
      enqueueSnackbar({
        message: 'Saved, but the student list could not be refreshed. Reload the page to see the change.',
        variant: 'warning'
      })
      return null
    }
  }

  const loadLinkedStudents = async () => {
    try {
      setLoading(true)

      const linkedStudents = await fetchLinkedStudents()

      setStudents(linkedStudents)
      loadBillingSummary()

      // Phase 1 + Phase 3: the blocking name modal, then the dismissible
      // onboarding-choice modal, both take priority over every other
      // onboarding surface (locked decision #13 in the overview doc:
      // Competition / What's New wait until name + add-student/demo choice
      // are done/dismissed). Until both resolve, onboardingDetermined is
      // deliberately left false so the tutorial / checklist / competition
      // modal decide for the first time only once userProfile reflects that
      // resolution.
      const nameModalPending = userProfile?.pendingParentName === true
      const onboardingChoiceStillPending = userProfile?.pendingOnboardingChoice === true
      const blockingOnboardingSurface = nameModalPending || onboardingChoiceStillPending

      // Tutorial / checklist logic — determined once per session after profile is settled.
      // Show tutorial only when tutorialCompleted is not true.
      // Once the tutorial has been completed it is never auto-shown again.
      if (!onboardingDetermined.current && userProfile && !blockingOnboardingSurface) {
        onboardingDetermined.current = true
        const tutorialDone = userProfile.tutorialCompleted === true
        if (!tutorialDone) {
          setShowTutorial(true)
          setShowChecklist(false)
        } else {
          setShowTutorial(false)
          setShowChecklist(true)
        }
      }

      // Competition enrollment check — only if competition is still active
      const now = new Date()
      if (now < COMPETITION_END && !blockingOnboardingSurface) {
        try {
          // Read lightweight public status doc to detect early admin end
          const statusSnap = await getDoc(doc(db, 'competitionStatus', 'spring2026'))
          const competitionEnded = statusSnap.exists() && statusSnap.data().status === 'ended'

          if (!competitionEnded) {
            const enrolledIds = new Set()
            const parentStudents = linkedStudents.filter(s => isStudentRole(s.role))
            for (const student of parentStudents) {
              const entrySnap = await getDoc(doc(db, 'competitionEntries', student.id))
              if (entrySnap.exists()) {
                enrolledIds.add(student.id)
              }
            }
            setCompetitionEnrolledIds(enrolledIds)
            const unenrolled = parentStudents.filter(s => !enrolledIds.has(s.id))
            // Suppress modal if dismissed in the current sign-in session.
            // competitionPromptDismissedAt is written to Firestore on dismiss;
            // we compare it to lastSignInTime so the prompt re-shows on next sign-in.
            const lastSignInMs = currentUser?.metadata?.lastSignInTime
              ? new Date(currentUser.metadata.lastSignInTime).getTime() : 0
            const dismissedAtMs = userProfile?.competitionPromptDismissedAt?.toMillis
              ? userProfile.competitionPromptDismissedAt.toMillis()
              : (userProfile?.competitionPromptDismissedAt?._seconds
                ? userProfile.competitionPromptDismissedAt._seconds * 1000 : 0)
            const dismissedThisSession = dismissedAtMs > 0 && dismissedAtMs > lastSignInMs
            // Show for unenrolled students, OR for new parents who haven't finished the
            // tutorial yet (account-creation path) so they learn about the competition.
            const isNewParent = userProfile?.tutorialCompleted !== true
            const hasUnenrolled = unenrolled.length > 0
            if ((hasUnenrolled || isNewParent) && !dismissedThisSession && !sessionStorage.getItem('competitionPromptShown')) {
              const tutorialAlreadyDone = userProfile?.tutorialCompleted === true
              if (tutorialAlreadyDone) {
                setTimeout(() => setShowCompetitionModal(true), 600)
              } else {
                competitionModalReady.current = true
              }
            }
          }
        } catch (e) {
          console.error('Error loading competition enrollment:', e)
        }
      }

      // Load parent's phone number if exists and format it for display
      if (userProfile?.phoneNumber) {
        // If stored in E.164 format (+15555555555), format it for display
        const storedNumber = userProfile.phoneNumber
        if (storedNumber.startsWith('+') && storedNumber.length === 12) {
          // Format from E.164 to display format
          setPhoneNumber(formatPhoneNumber(storedNumber))
        } else {
          setPhoneNumber(storedNumber)
        }
      }
    } catch (error) {
      console.error('Error loading linked students:', error)
      enqueueSnackbar({
        message: 'Error loading students',
        variant: 'error'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleEnrollStudents = async (studentIds) => {
    try {
      const enrollFn = httpsCallable(functions, 'enrollStudentsInCompetition')
      await enrollFn({ studentIds })
      setCompetitionEnrolledIds(prev => {
        const next = new Set(prev)
        studentIds.forEach(id => next.add(id))
        return next
      })
      enqueueSnackbar({
        message: 'Students enrolled in the competition!',
        variant: 'success'
      })
    } catch (e) {
      console.error('Error enrolling students:', e)
      enqueueSnackbar({
        message: 'Failed to enroll students. Please try again.',
        variant: 'error'
      })
    } finally {
      sessionStorage.setItem('competitionPromptShown', 'true')
      setShowCompetitionModal(false)
    }
  }

  const handleDismissCompetitionModal = async () => {
    sessionStorage.setItem('competitionPromptShown', 'true')
    setShowCompetitionModal(false)
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        competitionPromptDismissedAt: serverTimestamp()
      })
    } catch (e) {
      console.error('Error saving competition prompt dismissal:', e)
    }
  }

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '')
    
    // Limit to 11 digits (1 for country code + 10 for phone)
    const limitedDigits = digits.slice(0, 11)
    
    // Format based on number of digits
    if (limitedDigits.length === 0) {
      return ''
    } else if (limitedDigits.length <= 1) {
      return `+${limitedDigits}`
    } else if (limitedDigits.length <= 4) {
      return `+${limitedDigits[0]} (${limitedDigits.slice(1)}`
    } else if (limitedDigits.length <= 7) {
      return `+${limitedDigits[0]} (${limitedDigits.slice(1, 4)}) ${limitedDigits.slice(4)}`
    } else {
      return `+${limitedDigits[0]} (${limitedDigits.slice(1, 4)}) ${limitedDigits.slice(4, 7)}-${limitedDigits.slice(7)}`
    }
  }

  const handlePhoneNumberChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value)
    setPhoneNumber(formatted)
  }

  const handleSavePhoneNumber = async () => {
    // Extract only digits for validation
    const digitsOnly = phoneNumber.replace(/\D/g, '')
    
    if (digitsOnly.length !== 11) {
      enqueueSnackbar({
        message: 'Please enter a valid 11-digit phone number',
        variant: 'error'
      })
      return
    }

    // Convert to E.164 format: +15555555555
    const e164Format = `+${digitsOnly}`

    try {
      setSavingPhone(true)
      const { doc, updateDoc } = await import('firebase/firestore')
      const parentDocRef = doc(db, 'users', currentUser.uid)
      
      await updateDoc(parentDocRef, {
        phoneNumber: e164Format
      })

      enqueueSnackbar({
        message: 'Phone number saved successfully!',
        variant: 'success'
      })

      setShowPhoneModal(false)
      setSmsConsent(false)
    } catch (error) {
      console.error('Error saving phone number:', error)
      enqueueSnackbar({
        message: 'Error saving phone number',
        variant: 'error'
      })
    } finally {
      setSavingPhone(false)
    }
  }



  const handleLogout = async () => {
    try {
      await logout()
      enqueueSnackbar({
        message: 'Logged out successfully',
        variant: 'success'
      })
      history.replace('/')
    } catch (error) {
      enqueueSnackbar({
        message: 'Error logging out',
        variant: 'error'
      })
    }
  }



  const loadStudentQuestions = async (studentId) => {
    try {
      setLoadingQuestions(true)
      
      const questionsRef = collection(db, 'users', studentId, 'studyQuestions')
      const q = query(questionsRef, orderBy('timestamp', 'desc'))
      const questionsSnapshot = await getDocs(q)

      const questions = questionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      const allQuestions = [...questions]

      const answeredPageKeys = new Set()
      questions.forEach(q => {
        if (q.sourceType === 'page' && q.pageId) {
          const qText = q.question?.text || q.question?.latex || ''
          answeredPageKeys.add(`${q.pageId}_${qText}`)
        }
      })

      try {
        const pagesRef = collection(db, 'users', studentId, 'pages')
        const pagesQ = query(pagesRef, orderBy('pageNumber', 'asc'))
        const pagesSnapshot = await getDocs(pagesQ)

        pagesSnapshot.docs.forEach(pageDoc => {
          const pageData = pageDoc.data()
          if (pageData.questionBank && pageData.questionBank.length > 0) {
            pageData.questionBank.forEach((qItem, idx) => {
              const pageKey = `${pageDoc.id}_${qItem.text}`
              if (!answeredPageKeys.has(pageKey)) {
                allQuestions.push({
                  id: `page_${pageDoc.id}_${idx}`,
                  question: { text: qItem.text, latex: qItem.text },
                  topic: qItem.topic || null,
                  sourceLabel: `Page ${pageData.pageNumber}`,
                  sourceType: 'page',
                  timestamp: pageData.createdAt || null
                })
              }
            })
          }
        })
      } catch (pageError) {
        console.error('Error loading page questions for view work:', pageError)
      }

      setStudyQuestions(allQuestions)

      const firstWithAttempts = allQuestions.find(q =>
        (q.attempts && q.attempts.length > 0) ||
        (q.allAttempts && q.allAttempts.length > 0) ||
        (q.answer || q.feedback || q.studentAnswer)
      )
      if (firstWithAttempts) {
        setSelectedViewWorkQuestion(firstWithAttempts)
        loadDesktopVideoRecommendations(firstWithAttempts.id, studentId)
      } else {
        setSelectedViewWorkQuestion(null)
        enqueueSnackbar({
          message: 'No study questions found for this student',
          variant: 'info'
        })
      }
    } catch (error) {
      console.error('Error loading study questions:', error)
      enqueueSnackbar({
        message: `Error loading study questions: ${error.message}`,
        variant: 'error'
      })
    } finally {
      setLoadingQuestions(false)
    }
  }

  const handleStudentClick = async (student) => {
    setSelectedStudent(student)
    await loadStudentQuestions(student.id)
    trackFathomEvent('parent_view_student_work')
    
    // Load AI Chat Helper setting
    setAiChatHelperEnabled(student.aiChatHelperEnabled !== false)
    setReportsEnabled(student.reportsEnabled !== false)
    setReportScreencapEnabled(student.reportScreencapEnabled !== false)
  }

  const handleBackToStudents = () => {
    setSelectedStudent(null)
    setStudyQuestions([])
    setSelectedViewWorkQuestion(null)
    setShowManageStudentDropdown(false)
  }

  const handleToggleAiChatHelper = async () => {
    if (!selectedStudent) return
    
    try {
      const { doc, updateDoc } = await import('firebase/firestore')
      const studentDocRef = doc(db, 'users', selectedStudent.id)
      
      const newValue = !aiChatHelperEnabled
      await updateDoc(studentDocRef, {
        aiChatHelperEnabled: newValue
      })
      
      setAiChatHelperEnabled(newValue)
      trackFathomEvent(newValue ? 'parent_enable_ai_helper' : 'parent_disable_ai_helper')
      enqueueSnackbar({
        message: `AI Chat Helper ${newValue ? 'enabled' : 'disabled'}`,
        variant: 'success'
      })
    } catch (error) {
      console.error('Error updating AI Chat Helper setting:', error)
      enqueueSnackbar({
        message: 'Error updating setting',
        variant: 'error'
      })
    }
  }

  const applyStudentFlag = (studentId, field, value) => {
    setStudents((prev) => prev.map((student) => (
      student.id === studentId ? { ...student, [field]: value } : student
    )))
    setSelectedStudent((prev) => (
      prev && prev.id === studentId ? { ...prev, [field]: value } : prev
    ))
  }

  const handleToggleStudentReportSetting = async (field, currentValue, setter, enabledMessage, disabledMessage) => {
    if (!selectedStudent) return
    const newValue = !currentValue
    try {
      const { doc, updateDoc } = await import('firebase/firestore')
      await updateDoc(doc(db, 'users', selectedStudent.id), {
        [field]: newValue
      })
      setter(newValue)
      applyStudentFlag(selectedStudent.id, field, newValue)
      enqueueSnackbar({
        message: newValue ? enabledMessage : disabledMessage,
        variant: 'success'
      })
    } catch (error) {
      console.error(`Error updating ${field}:`, error)
      enqueueSnackbar({
        message: 'Error updating setting',
        variant: 'error'
      })
    }
  }

  const handleSendPasswordReset = async () => {
    if (!selectedStudent) return
    setSendingPasswordReset(true)
    setShowManageStudentDropdown(false)
    try {
      const result = await sendStudentPasswordReset(selectedStudent.id)
      const destination = result.sentTo || (result.isParentEmail ? 'your email' : `${selectedStudent.firstName}'s email`)
      enqueueSnackbar({
        message: `Password reset email sent to ${destination}`,
        variant: 'success'
      })
    } catch (error) {
      console.error('Error sending password reset:', error)
      enqueueSnackbar({
        message: error.message || 'Error sending password reset email',
        variant: 'error'
      })
    } finally {
      setSendingPasswordReset(false)
    }
  }

  const handleEmailPrefToggle = async (key, currentValue, setter) => {
    if (savingEmailPrefs) return
    const newValue = !currentValue
    setter(newValue)
    setSavingEmailPrefs(true)
    try {
      const parentDocRef = doc(db, 'users', currentUser.uid)
      await updateDoc(parentDocRef, {
        [`emailNotifications.${key}`]: newValue
      })
    } catch (error) {
      console.error('Error saving email preference:', error)
      setter(currentValue)
      enqueueSnackbar({ message: 'Error saving preference', variant: 'error' })
    } finally {
      setSavingEmailPrefs(false)
    }
  }

  // completeParentName already clears pendingParentName on the profile in
  // memory, which hides this modal and reveals the invite. The server read
  // catches up in the background so the name step does not wait on it.
  const handleNameModalComplete = () => {
    refreshUserProfile()
  }

  // Phase 3/5: resolves the post-name "Add a student / See a demo" choice.
  // All three exits (add_student, demo, dismissed) call the same server
  // callable, then await a profile refresh.
  //
  // The destination surface (Add Student / the demo) is opened FIRST, before
  // that refresh, not after. refreshUserProfile flips pendingOnboardingChoice
  // to false, which is exactly what lets loadLinkedStudents decide the legacy
  // ParentTutorial/ParentChecklist for the first time (locked decisions
  // #12/#13) — that decision runs in a passive effect whose timing relative
  // to this async function's own continuation is not guaranteed. Opening the
  // destination before the refresh (rather than after) means the tutorial/
  // checklist/competition render guards always see the destination already
  // open, so nothing can flash underneath or stack on top of it no matter how
  // the effect is scheduled.
  const handleOnboardingChoiceSelect = async (choice) => {
    if (resolvingOnboardingChoice) return
    setResolvingOnboardingChoice(true)

    if (choice === 'add_student') {
      handleAddStudent()
    } else if (choice === 'demo') {
      setShowDemo(true)
    }

    try {
      await completeOnboardingChoice(choice === 'dismissed')
    } catch (error) {
      console.error('Error resolving onboarding choice:', error)
      enqueueSnackbar({
        message: error.message || 'Something went wrong. Please try again.',
        variant: 'error'
      })
      setResolvingOnboardingChoice(false)
      if (choice === 'add_student') {
        handleCloseCreateStudentModal()
      } else if (choice === 'demo') {
        setShowDemo(false)
      }
      return
    }

    await refreshUserProfile()
    setResolvingOnboardingChoice(false)

    trackFathomEvent(`parent_onboarding_choice_${choice}`)
  }

  const handleOpenDemo = () => {
    setShowDemo(true)
  }

  const handleCloseDemo = () => {
    setShowDemo(false)
  }

  // demoTutorialCompleted mirrors tutorialCompleted: a plain client-writable
  // profile flag (not server-owned in firestore.rules) because it only gates
  // whether a non-persisting, purely client-side overlay auto-opens again —
  // there is no real student data or gamification total behind it to protect.
  const handleDemoComplete = async () => {
    try {
      const parentRef = doc(db, 'users', currentUser.uid)
      await updateDoc(parentRef, { demoTutorialCompleted: true })
      await refreshUserProfile()
      trackFathomEvent('parent_demo_tutorial_completed')
    } catch (error) {
      console.error('Error marking demo tutorial complete:', error)
    }
  }

  // ── Student View (Phase 4) ─────────────────────────────────────────────────
  // Entering, leaving, and the PIN are all server decisions. Nothing here is
  // trusted on its own: the session document authorizes the writes, and this
  // component only reflects what the callables report back.

  const studentViewStudent = studentViewStudentId
    ? students.find(student => student.id === studentViewStudentId) || null
    : null
  const studentViewActive = !!studentViewStudent
  const parentPinSet = userProfile?.studentViewPinSet === true

  // A restored session names a child before the roster has arrived. Until those
  // two agree there is nothing safe to render: that gap is precisely when a
  // child could otherwise glimpse — or tap — parent-only navigation.
  const studentViewResolving = studentViewRestorePending ||
    (!!studentViewStudentId && !studentViewStudent && loading)

  // Restores Student View after a reload. Without this a child could leave the
  // lock simply by refreshing the page, since the parent's Firebase session
  // survives a refresh while component state does not. Started immediately
  // rather than after the roster so it costs no extra time on a normal visit.
  useEffect(() => {
    if (studentViewRestoreAttempted.current) return
    if (!currentUser) {
      setStudentViewRestorePending(false)
      return
    }
    studentViewRestoreAttempted.current = true

    const restoreStudentViewSession = async () => {
      try {
        const sessionSnap = await getDoc(doc(db, 'studentViewSessions', currentUser.uid))
        if (!sessionSnap.exists()) return

        const session = sessionSnap.data()
        const expiresAtMs = session.expiresAt?.toMillis
          ? session.expiresAt.toMillis()
          : 0
        if (!session.activeStudentId || expiresAtMs <= Date.now()) return

        setStudentViewStudentId(session.activeStudentId)
        setStudentViewPinProtected(session.pinProtected === true)

        // Re-opening the session extends its expiry, so a child who has been
        // working since before the reload is not cut off mid-question.
        const refreshed = await startStudentViewSession(session.activeStudentId)
        setStudentViewPinProtected(refreshed?.pinSet === true)
      } catch (error) {
        console.error('Error restoring Student View session:', error)
      } finally {
        setStudentViewRestorePending(false)
      }
    }

    restoreStudentViewSession()
  }, [currentUser, startStudentViewSession])

  // Lets app-level chrome (What's New today) know the screen belongs to a child
  // right now. Cleared on unmount so navigating away can never leave the flag
  // stuck on for the parent. The acting student id is mirrored too so
  // lastActiveAt can be written on view-only children who have no Auth user.
  useEffect(() => {
    setStudentViewActive(studentViewActive)
    setStudentViewActingStudentId(studentViewStudentId || null)
    return () => {
      setStudentViewActive(false)
      setStudentViewActingStudentId(null)
    }
  }, [
    studentViewActive,
    studentViewStudentId,
    setStudentViewActive,
    setStudentViewActingStudentId
  ])

  // Mirrors the demo modal, the Add Student modal, and an expanded checklist
  // panel into the App-level context so chrome mounted outside ParentDashboard
  // (What's New) can defer the same way it already defers for Student View —
  // see the AuthContext comment on parentTaskModalActive. The checklist panel
  // is included because it auto-opens immediately after the legacy tutorial
  // completes, at the same moment the delayed competition-enrollment prompt
  // becomes eligible; without this, release notes could render behind it the
  // instant that happens.
  useEffect(() => {
    const active = showDemo || showCreateStudentModal || checklistPanelOpen
    setParentTaskModalActive(active)
    return () => setParentTaskModalActive(false)
  }, [showDemo, showCreateStudentModal, checklistPanelOpen, setParentTaskModalActive])

  // Keeps a long sitting alive. The session expires server-side on purpose, so
  // it is extended while the child is actually working rather than granted for
  // an unbounded period up front.
  useEffect(() => {
    if (!studentViewActive) return

    const EXTEND_INTERVAL_MS = 30 * 60 * 1000
    const timer = setInterval(() => {
      startStudentViewSession(studentViewStudentId).catch((error) => {
        console.error('Error extending Student View session:', error)
      })
    }, EXTEND_INTERVAL_MS)

    return () => clearInterval(timer)
  }, [studentViewActive, studentViewStudentId, startStudentViewSession])

  const handleEnterStudentView = async (student) => {
    if (!student || enteringStudentView) return
    setEnteringStudentView(true)
    try {
      const result = await startStudentViewSession(student.id)
      setStudentViewStudentId(student.id)
      setStudentViewPinProtected(result?.pinSet === true)
      setShowManageStudentDropdown(false)
      trackFathomEvent('parent_enter_student_view')
      // Offered once per account, the first time Student View is ever opened.
      if (result?.offerPinSetup) {
        setShowPinOffer(true)
        await refreshUserProfile()
      }
    } catch (error) {
      console.error('Error entering Student View:', error)
      enqueueSnackbar({
        message: error.message || 'Could not open Student View',
        variant: 'error'
      })
    } finally {
      setEnteringStudentView(false)
    }
  }

  // The exit control. With a PIN set this only opens the keypad — the session
  // (and every write grant with it) stays until the server accepts the PIN.
  const handleRequestExitStudentView = () => {
    setPinError(null)
    setPinNotice(null)
    if (studentViewPinProtected) {
      setPinModalMode('verify')
      return
    }
    setShowLeaveStudentViewConfirm(true)
  }

  const closeStudentView = async () => {
    setStudentViewStudentId(null)
    setStudentViewPinProtected(false)
    setShowLeaveStudentViewConfirm(false)
    setPinModalMode(null)
    setSelectedMenuItem('my-students')
    // The student's totals and work changed while the parent was inside, so the
    // profile they land back on is re-read rather than shown stale.
    await refreshStudents(studentViewStudentId)
  }

  const handleLeaveStudentViewWithoutPin = async () => {
    if (leavingStudentView) return
    setLeavingStudentView(true)
    try {
      await endStudentViewSession()
      trackFathomEvent('parent_exit_student_view')
      await closeStudentView()
    } catch (error) {
      console.error('Error leaving Student View:', error)
      enqueueSnackbar({
        message: error.message || 'Could not leave Student View',
        variant: 'error'
      })
    } finally {
      setLeavingStudentView(false)
    }
  }

  // ── Parent PIN ────────────────────────────────────────────────────────────

  const openPinModal = (mode) => {
    setPinError(null)
    setPinNotice(null)
    setPinModalMode(mode)
  }

  const closePinModal = () => {
    setPinModalMode(null)
    setPinError(null)
    setPinNotice(null)
  }

  // Every PIN operation lands here. The modal collects digits; which callable
  // runs is decided by the mode, and the PIN is never stored anywhere locally.
  const handlePinSubmit = async ({ pin, currentPin, newPin, code, removePin }) => {
    if (pinBusy) return
    setPinBusy(true)
    setPinError(null)
    try {
      if (pinModalMode === 'verify') {
        await endStudentViewSession(pin)
        trackFathomEvent('parent_exit_student_view')
        await closeStudentView()
        return
      }

      if (pinModalMode === 'set') {
        await setParentPin(newPin)
        setStudentViewPinProtected(true)
        setShowPinOffer(false)
        enqueueSnackbar({ message: 'Student View PIN saved', variant: 'success' })
      } else if (pinModalMode === 'change') {
        await changeParentPin(currentPin, newPin)
        setStudentViewPinProtected(true)
        enqueueSnackbar({ message: 'Student View PIN changed', variant: 'success' })
      } else if (pinModalMode === 'remove') {
        await removeParentPin(pin)
        setStudentViewPinProtected(false)
        enqueueSnackbar({ message: 'Student View PIN removed', variant: 'success' })
      } else if (pinModalMode === 'reset') {
        const result = await confirmParentPinReset(code, removePin ? null : newPin)
        setStudentViewPinProtected(result?.pinSet === true)
        enqueueSnackbar({
          message: result?.pinSet
            ? 'Student View PIN updated'
            : 'Student View PIN removed',
          variant: 'success'
        })
      }

      await refreshUserProfile()
      closePinModal()
    } catch (error) {
      console.error(`Error handling PIN action (${pinModalMode}):`, error)
      setPinError(error.message || 'Something went wrong. Please try again.')
    } finally {
      setPinBusy(false)
    }
  }

  // Forgot PIN: the code goes to the parent's Auth email, so a parent locked
  // out on the family tablet can finish the reset from any device.
  const handleSendPinResetCode = async () => {
    if (sendingResetCode) return
    setSendingResetCode(true)
    setPinError(null)
    try {
      const result = await requestParentPinReset()
      setPinNotice(
        `We emailed a 6-digit code to ${result?.sentTo || 'your email address'}. ` +
        'It expires in 15 minutes and works on any device.'
      )
      setPinModalMode('reset')
    } catch (error) {
      console.error('Error requesting PIN reset:', error)
      setPinError(error.message || 'Could not send the reset email')
    } finally {
      setSendingResetCode(false)
    }
  }

  const handleTutorialComplete = () => {
    setShowTutorial(false)
    setShowChecklist(true)
    setChecklistAutoOpen(true)
  }

  const handleRestartTutorial = async () => {
    try {
      const { doc: firestoreDoc, updateDoc: firestoreUpdateDoc } = await import('firebase/firestore')
      const userRef = firestoreDoc(db, 'users', currentUser.uid)
      await firestoreUpdateDoc(userRef, { tutorialCompleted: false })
    } catch (err) {
      console.error('Error resetting tutorial:', err)
    }
    setShowChecklist(false)
    setShowTutorial(true)
  }

  const handleAddStudent = () => {
    setShowCreateStudentModal(true)
  }

  const handleCloseCreateStudentModal = () => {
    setShowCreateStudentModal(false)
    setStudentFormData({
      firstName: '',
      username: '',
      password: '',
      email: ''
    })
    setShowPasswordInModal(false)
    setUseParentEmail(false)
    setStudentViewOnly(false)
    setEnrollInCompetition(true)
  }

  // Turning the toggle on clears anything already typed into the credential
  // fields: the callable rejects a credential-light create that carries an
  // email, username, or password, and a stale value must never be resurrected
  // by toggling back and forth.
  const handleToggleStudentViewOnly = (checked) => {
    setStudentViewOnly(checked)
    if (checked) {
      setStudentFormData(prev => ({
        ...prev,
        username: '',
        password: '',
        email: ''
      }))
      setUseParentEmail(false)
      setShowPasswordInModal(false)
    }
  }

  const validatePassword = (password) => {
    const hasMinLength = password.length >= 8
    const hasNumber = /\d/.test(password)
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password)
    return hasMinLength && hasNumber && hasSpecialChar
  }

  const resetLinkCredentialsForm = () => {
    setLinkFormData({ username: '', password: '', email: '' })
    setLinkUseParentEmail(false)
    setShowLinkPassword(false)
  }

  // Never carry a half-typed login (least of all a password) from one child's
  // settings to another's.
  const selectedStudentId = selectedStudent?.id
  useEffect(() => {
    resetLinkCredentialsForm()
  }, [selectedStudentId])

  useEffect(() => {
    setSettingsFirstName(selectedStudent?.firstName || '')
  }, [selectedStudentId, selectedStudent?.firstName])

  const handleSaveLinkedFirstName = async () => {
    if (!selectedStudent || savingSettingsFirstName) return
    setSavingSettingsFirstName(true)
    try {
      const setName = httpsCallable(functions, 'setLinkedStudentFirstName')
      const result = await setName({
        studentId: selectedStudent.id,
        firstName: settingsFirstName
      })
      setSettingsFirstName(result.data?.firstName || '')
      await refreshStudents(selectedStudent.id)
      enqueueSnackbar({ message: 'Saved', variant: 'success' })
    } catch (error) {
      enqueueSnackbar({
        message: error.message || 'Could not save the first name',
        variant: 'error'
      })
    } finally {
      setSavingSettingsFirstName(false)
    }
  }

  const handleCreateStudent = async () => {
    const firstName = studentFormData.firstName.trim()
    const email = studentFormData.email.trim()
    const username = studentFormData.username.trim()

    if (!firstName) {
      enqueueSnackbar({
        message: 'Please enter a first name',
        variant: 'error'
      })
      return
    }

    // A credential-light student needs nothing but a first name; everything
    // below only applies to a student who will sign in themselves.
    // Username is required only when no student email is provided.
    const needsUsername = !studentViewOnly && (useParentEmail || !email)

    if (!studentViewOnly) {
      if (!studentFormData.password || (needsUsername && !username)) {
        enqueueSnackbar({
          message: 'Please fill in all required fields',
          variant: 'error'
        })
        return
      }

      if (!validatePassword(studentFormData.password)) {
        enqueueSnackbar({
          message: 'Password must be at least 8 characters with 1 number and 1 special character',
          variant: 'error'
        })
        return
      }

      if (!useParentEmail && email && !EMAIL_PATTERN.test(email)) {
        enqueueSnackbar({
          message: 'Please enter a valid email address',
          variant: 'error'
        })
        return
      }
    }

    try {
      setCreatingStudent(true)

      const createResult = studentViewOnly
        ? await createStudentAccount({
          firstName,
          lastName: userProfile.lastName || '',
          studentViewOnly: true
        })
        : await createStudentAccount({
          firstName,
          lastName: userProfile.lastName || '',
          // When a student email is provided the username is auto-generated
          // server-side from its prefix.
          username: needsUsername ? username : null,
          password: studentFormData.password,
          email: !useParentEmail ? (email || null) : null
        })

      const newStudentId = typeof createResult?.userId === 'string' ? createResult.userId : ''
      // New parent-created students start on Free. Open that choice now, before
      // competition enrollment or any other modal. Stay on Free does not bill.
      if (newStudentId) {
        setTierTarget({
          student: {
            id: newStudentId,
            firstName,
            lastName: (userProfile && userProfile.lastName) || ''
          },
          summary: {
            studentId: newStudentId,
            access: 'free',
            renewalAt: null,
            cancelAtPeriodEnd: false
          }
        })
      }
      handleCloseCreateStudentModal()

      // Enroll in competition if the checkbox is checked and competition is still active.
      // The upgrade choice is already open; this must not cover it.
      let enrolledSuccessfully = false
      if (enrollInCompetition && new Date() < COMPETITION_END && newStudentId) {
        try {
          const enrollFn = httpsCallable(functions, 'enrollStudentsInCompetition')
          await enrollFn({ studentIds: [newStudentId] })
          setCompetitionEnrolledIds(prev => new Set(prev).add(newStudentId))
          enrolledSuccessfully = true
        } catch (enrollErr) {
          console.error('Error enrolling new student in competition:', enrollErr)
          enqueueSnackbar({
            message: 'Student created, but competition enrollment failed. You can enroll them from the XP Competition menu.',
            variant: 'warning'
          })
        }
      }

      trackFathomEvent(studentViewOnly ? 'parent_create_light_student' : 'parent_create_student')
      enqueueSnackbar({
        message: enrolledSuccessfully
          ? 'Student added and enrolled in the XP Competition!'
          : 'Student added successfully!',
        variant: 'success'
      })

      await refreshStudents()
    } catch (error) {
      console.error('Error creating student account:', error)
      enqueueSnackbar({
        message: error.message || 'Error creating student account',
        variant: 'error'
      })
    } finally {
      setCreatingStudent(false)
    }
  }

  const handleLinkStudentCredentials = async () => {
    if (!selectedStudent) return

    const email = linkFormData.email.trim()
    const username = linkFormData.username.trim()
    const needsUsername = linkUseParentEmail || !email

    if (!linkFormData.password || (needsUsername && !username)) {
      enqueueSnackbar({
        message: 'Please fill in all required fields',
        variant: 'error'
      })
      return
    }

    if (!validatePassword(linkFormData.password)) {
      enqueueSnackbar({
        message: 'Password must be at least 8 characters with 1 number and 1 special character',
        variant: 'error'
      })
      return
    }

    if (!linkUseParentEmail && email && !EMAIL_PATTERN.test(email)) {
      enqueueSnackbar({
        message: 'Please enter a valid email address',
        variant: 'error'
      })
      return
    }

    const studentId = selectedStudent.id

    try {
      setLinkingCredentials(true)

      await linkStudentCredentials({
        studentId,
        username: needsUsername ? username : null,
        email: !linkUseParentEmail ? (email || null) : null,
        password: linkFormData.password
      })

      trackFathomEvent('parent_link_student_credentials')
      enqueueSnackbar({
        message: `${selectedStudent.firstName} can now sign in on their own. Their work is unchanged.`,
        variant: 'success'
      })

      resetLinkCredentialsForm()
      await refreshStudents(studentId)
    } catch (error) {
      console.error('Error adding student login:', error)
      enqueueSnackbar({
        message: error.message || 'Error adding student login',
        variant: 'error'
      })
    } finally {
      setLinkingCredentials(false)
    }
  }

  const loadPages = async (selectPageId = null) => {
    if (!selectedStudent) return
    try {
      setLoadingPages(true)
      const pagesRef = collection(db, 'users', selectedStudent.id, 'pages')
      const q = query(pagesRef, orderBy('pageNumber', 'asc'))
      const snapshot = await getDocs(q)
      const loaded = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
      setPages(loaded)
      if (selectPageId) {
        const target = loaded.find(p => p.id === selectPageId)
        if (target) setSelectedPage(target)
      } else if (loaded.length > 0) {
        setSelectedPage(prev => {
          if (!prev) return loaded[0]
          const refreshed = loaded.find(p => p.id === prev.id)
          return refreshed || loaded[0]
        })
      }
    } catch (error) {
      console.error('Error loading pages:', error)
    } finally {
      setLoadingPages(false)
    }
  }

  const handlePageImageUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      setPageImageSrc(reader.result)
      setShowPageImageCrop(true)
    }
    reader.readAsDataURL(file)
    e.target.value = ''
  }

  const handlePageImageCropComplete = async (croppedBlob) => {
    setShowPageImageCrop(false)
    if (!selectedStudent) return

    // Normalize once and use this value for both the object name and the
    // Firestore field. The number input still admits "07" and "7.5", which
    // previously stored page 7 while naming the image page_07/page_7.5.
    const pageNumberValue = parseInt(pageNumber, 10)
    if (!Number.isFinite(pageNumberValue) || pageNumberValue < 1) {
      enqueueSnackbar({ message: 'Please enter a valid page number', variant: 'error' })
      return
    }

    setProcessingPage(true)
    setPageCreationProgress(0)
    setPageCreationStep('Reading page...')
    try {
      const result = await processScreenshotWithMathpix(croppedBlob)
      if (!result.formattedQuestions || result.formattedQuestions.length === 0) {
        enqueueSnackbar({ message: 'No questions found on this page', variant: 'warning' })
        return
      }

      setPageCreationProgress(25)
      setPageCreationStep('Analysing questions...')

      const analyzeStudyQuestion = httpsCallable(functions, 'analyzeStudyQuestion')
      const totalQuestions = result.formattedQuestions.length
      let completedQuestions = 0
      const questionBank = await Promise.all(
        result.formattedQuestions.map(async (q) => {
          try {
            const analysis = await analyzeStudyQuestion({
              questionLatex: q.question,
              questionText: q.question
            })
            completedQuestions++
            setPageCreationProgress(25 + Math.round((completedQuestions / totalQuestions) * 40))
            return {
              text: q.question,
              mathBranch: analysis.data.mathBranch,
              topic: analysis.data.topic,
              difficulty: analysis.data.difficulty,
              questionHash: analysis.data.questionHash
            }
          } catch (err) {
            completedQuestions++
            setPageCreationProgress(25 + Math.round((completedQuestions / totalQuestions) * 40))
            return {
              text: q.question,
              mathBranch: 'Unknown',
              topic: 'Unknown',
              difficulty: 'medium',
              questionHash: null
            }
          }
        })
      )

      setPageCreationProgress(70)
      setPageCreationStep('Uploading image...')
      const pageImageRef = ref(storage, pageImagePath(currentUser.uid, selectedStudent.id, pageNumberValue))
      await uploadBytes(pageImageRef, croppedBlob)
      const pageImageUrl = await getDownloadURL(pageImageRef)

      setPageCreationProgress(85)
      setPageCreationStep('Generating summary...')
      let description = ''
      try {
        const pageDescToken = await auth.currentUser.getIdToken()
        const descResponse = await fetch('/api/generate-page-description', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${pageDescToken}`
          },
          body: JSON.stringify({ questions: questionBank.map(q => q.text) })
        })
        const descResult = await descResponse.json()
        if (descResult.success) {
          description = descResult.description
        }
      } catch (err) {
        console.error('Error generating description:', err)
      }

      setPageCreationProgress(95)
      setPageCreationStep('Saving page...')
      const newPageRef = await addDoc(collection(db, 'users', selectedStudent.id, 'pages'), {
        pageNumber: pageNumberValue,
        imageUrl: pageImageUrl,
        questionBank,
        description,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid
      })

      setPageCreationProgress(100)
      enqueueSnackbar({ message: `Page ${pageNumberValue} added with ${questionBank.length} question(s)`, variant: 'success' })
      setPageNumber('')
      setShowPageNumberModal(false)
      await loadPages(newPageRef.id)
    } catch (error) {
      console.error('Error processing page:', error)
      enqueueSnackbar({ message: 'Error processing page', variant: 'error' })
    } finally {
      setProcessingPage(false)
      setPageCreationProgress(0)
      setPageCreationStep('')
      setPageImageSrc(null)
    }
  }

  const handleStartAddPage = () => {
    setShowPageNumberModal(true)
  }

  const handleDeletePage = async (page) => {
    try {
      await deleteDoc(doc(db, 'users', selectedStudent.id, 'pages', page.id))
      try {
        const imageRef = ref(storage, pageImagePath(currentUser.uid, selectedStudent.id, page.pageNumber))
        await deleteObject(imageRef)
      } catch (storageErr) {
        console.warn('Could not delete page image from storage:', storageErr)
      }
      setSelectedPage(null)
      setConfirmDeletePageId(null)
      await loadPages()
      enqueueSnackbar({ message: `Page ${page.pageNumber} deleted`, variant: 'success' })
    } catch (error) {
      console.error('Error deleting page:', error)
      enqueueSnackbar({ message: 'Error deleting page', variant: 'error' })
    }
  }

  const handleConfirmPageNumber = () => {
    // Caught here so a bad number fails before the crop and Mathpix round trip,
    // not after. The input's min="1" only constrains its spinner, not typing.
    const parsedPageNumber = parseInt(pageNumber, 10)
    if (!Number.isFinite(parsedPageNumber) || parsedPageNumber < 1) {
      enqueueSnackbar({ message: 'Please enter a valid page number', variant: 'error' })
      return
    }
    setShowPageNumberModal(false)
    pageImageFileRef.current?.click()
  }

  const renderManageWorkContent = () => {
    return (
      <div className={`parent-desktop-view-work-layout ${selectedPage ? 'question-selected' : ''}`}>
        <div className="parent-desktop-questions-menu">
          <div className="parent-desktop-questions-menu-header">
            <div className="parent-desktop-questions-title">Pages</div>
            <button
              className="manage-work-add-page-header-btn"
              onClick={handleStartAddPage}
              disabled={processingPage}
              title="Add page"
            >
              {processingPage ? '…' : '+ Add'}
            </button>
          </div>
          {processingPage && (
            <div className="manage-work-progress-banner">
              <div className="manage-work-progress-label">
                <span className="manage-work-progress-step">{pageCreationStep}</span>
                <span className="manage-work-progress-pct">{pageCreationProgress}%</span>
              </div>
              <div className="manage-work-progress-track">
                <div
                  className="manage-work-progress-fill"
                  style={{ width: `${pageCreationProgress}%` }}
                />
              </div>
            </div>
          )}
          <div className="parent-desktop-questions-list-scroll">
            {loadingPages ? (
              <div className="manage-work-menu-empty">Loading...</div>
            ) : pages.length === 0 ? (
              <div className="manage-work-menu-empty">No pages yet. Add one to get started.</div>
            ) : (
              <div className="parent-desktop-date-section">
                {pages.map(page => {
                  const questionCount = page.questionBank?.length || 0
                  const isSelected = selectedPage?.id === page.id
                  return (
                    <div
                      key={page.id}
                      className={`parent-desktop-question-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => setSelectedPage(page)}
                    >
                      <div className="parent-desktop-question-preview">
                        Page {page.pageNumber}
                      </div>
                      {page.description && (
                        <div className="parent-desktop-question-source-label">{page.description}</div>
                      )}
                      <div className="manage-work-page-menu-count">
                        {questionCount} {questionCount === 1 ? 'question' : 'questions'}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        <div className="parent-desktop-answers-viewer">
          {selectedPage ? (
            <div className="manage-work-page-detail">
              <button
                className="parent-desktop-mobile-back-to-questions"
                onClick={() => setSelectedPage(null)}
              >
                ← Back to Pages
              </button>
              <div className="manage-work-page-detail-header">
                <div className="manage-work-page-detail-header-center">
                  <div className="manage-work-page-detail-title">Page {selectedPage.pageNumber}</div>
                  {selectedPage.description && (
                    <div className="manage-work-page-detail-description">{selectedPage.description}</div>
                  )}
                </div>
                <div className="manage-work-page-detail-header-actions">
                  {confirmDeletePageId === selectedPage.id ? (
                    <>
                      <span className="manage-work-delete-confirm-label">Delete this page?</span>
                      <button
                        className="manage-work-delete-confirm-btn"
                        onClick={() => handleDeletePage(selectedPage)}
                      >
                        Yes, delete
                      </button>
                      <button
                        className="manage-work-delete-cancel-btn"
                        onClick={() => setConfirmDeletePageId(null)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      className="manage-work-delete-page-btn"
                      onClick={() => setConfirmDeletePageId(selectedPage.id)}
                      title="Delete page"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
              <div className="manage-work-page-detail-questions">
                {selectedPage.questionBank && selectedPage.questionBank.length > 0 ? (
                  selectedPage.questionBank.map((q, idx) => (
                    <div key={idx} className="manage-work-page-detail-question-card">
                      <div className="manage-work-page-detail-question-header">
                        <div className="manage-work-page-detail-question-number">Q{idx + 1}</div>
                        {q.topic && (
                          <div className="manage-work-page-question-meta">
                            <span className="manage-work-tag">{q.topic}</span>
                          </div>
                        )}
                      </div>
                      <div className="manage-work-page-detail-question-text">
                        <MixedContentPreview content={q.text} fontSize="15px" />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="manage-work-empty">No questions extracted for this page.</div>
                )}
              </div>
            </div>
          ) : (
            <div className="manage-work-no-page-selected">
              <p>Select a page to view its questions</p>
            </div>
          )}
        </div>

        <input
          type="file"
          ref={pageImageFileRef}
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handlePageImageUpload}
        />
      </div>
    )
  }

  const handleBackToStudentsDesktop = () => {
    setSelectedStudent(null)
    setStudyQuestions([])
    setSelectedViewWorkQuestion(null)
    setShowManageStudentDropdown(false)
    setSelectedStudentMenuItem('view-work')
  }

  const openStudentTier = (student) => {
    if (!student || !student.id) return
    const summary = billingById[student.id]
    if (!summary || !tierAccessLabel(summary.access)) return
    setTierTarget({ student, summary })
  }

  const handleStudentClickDesktop = async (student) => {
    setSelectedStudent(student)
    await loadStudentQuestions(student.id)
    trackFathomEvent('parent_view_student_work')
    setAiChatHelperEnabled(student.aiChatHelperEnabled !== false)
    setReportsEnabled(student.reportsEnabled !== false)
    setReportScreencapEnabled(student.reportScreencapEnabled !== false)
    setSelectedStudentMenuItem('view-work')
  }

  const renderDesktopStudentsGrid = () => {
    if (loading) {
      return <div className="parent-desktop-loading">Loading students...</div>
    }

    if (students.length === 0) {
      return (
        <div className="parent-desktop-students-browser">
          <div className="parent-desktop-empty-state">
            <p>No students linked to your account yet.</p>
            <p className="parent-desktop-empty-subtitle">Create a student account or share your parent code to link accounts.</p>
          </div>
        </div>
      )
    }

    return (
      <div className="parent-desktop-students-browser">
        <div className="parent-desktop-students-grid">
          {students.map(student => (
            <div
              key={student.id}
              className="parent-desktop-student-card"
              onClick={() => handleStudentClickDesktop(student)}
            >
              <div className="parent-desktop-student-name">
                {studentRosterName(student)}
              </div>
              <div className="parent-desktop-student-email">
                {studentLoginLabel(student)}
              </div>
              {tierAccessLabel(billingById[student.id]?.access) && (
                <div className="parent-desktop-student-tier">
                  <span className="parent-desktop-student-tier-label">
                    {tierAccessLabel(billingById[student.id].access)}
                  </span>
                  <button
                    type="button"
                    className="save-button parent-settings-action-btn parent-desktop-student-tier-action"
                    aria-label={`${tierActionLabel(billingById[student.id].access)} ${studentRosterName(student)}`}
                    onClick={(event) => {
                      event.stopPropagation()
                      openStudentTier(student)
                    }}
                  >
                    {tierActionLabel(billingById[student.id].access)}
                  </button>
                </div>
              )}
            </div>
          ))}
          {isMobile && (
            <div
              className="parent-desktop-add-student-card"
              onClick={() => setShowCreateStudentModal(true)}
            >
              <AddIcon style={{ fontSize: 28 }} />
              <span>Add Student</span>
            </div>
          )}
        </div>
      </div>
    )
  }

  const handleViewWorkSortClick = () => {
    setShowViewWorkSortDropdown(!showViewWorkSortDropdown)
  }

  const handleViewWorkSortSelect = (sortType) => {
    setSortBy(sortType)
    setShowViewWorkSortDropdown(false)
  }

  const handleToggleViewWorkSortOrder = () => {
    setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')
  }

  const loadDesktopVideoRecommendations = async (questionId, studentId) => {
    try {
      const videosRef = collection(db, 'users', studentId, 'studyQuestions', questionId, 'videoRecommendations')
      const videosSnapshot = await getDocs(videosRef)
      const videos = videosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setDesktopVideoRecommendations(videos)
    } catch (error) {
      console.error('Error loading video recommendations:', error)
      setDesktopVideoRecommendations([])
    }
  }

  const handleSelectViewWorkQuestion = (question) => {
    setSelectedViewWorkQuestion(question)
    setDesktopVideoRecommendations([])
    if (question && selectedStudent) {
      trackFathomEvent('parent_view_question_detail')
      loadDesktopVideoRecommendations(question.id, selectedStudent.id)
    }
  }

  const renderDesktopQuestionCards = () => {
    if (loadingQuestions) {
      return <div className="parent-desktop-loading">Loading questions...</div>
    }

    if (studyQuestions.length === 0) {
      return (
        <div className="parent-desktop-empty-questions">
          <p>No study questions found for this student.</p>
        </div>
      )
    }

    const groupedQuestions = studyQuestions.reduce((groups, question) => {
      let groupKey
      if (sortBy === 'date') {
        if (question.timestamp?.toDate) {
          const date = question.timestamp.toDate()
          groupKey = date.toLocaleDateString('en-US', { 
            month: 'long', 
            day: 'numeric',
            year: 'numeric'
          })
        } else {
          groupKey = 'Unknown date'
        }
      } else if (sortBy === 'topic') {
        groupKey = question.topic || 'No Topic'
      } else if (sortBy === 'correctness') {
        const attempts = question.attempts || question.allAttempts || []
        const lastAttempt = attempts.length > 0 ? attempts[attempts.length - 1] : question
        const isCorrect = lastAttempt?.feedback?.isCorrect === true || question.feedback?.isCorrect === true
        groupKey = isCorrect ? 'Correct' : 'Incorrect'
      }

      if (!groups[groupKey]) {
        groups[groupKey] = []
      }
      groups[groupKey].push(question)
      return groups
    }, {})

    let sortedGroupKeys = Object.keys(groupedQuestions)
    if (sortBy === 'date') {
      sortedGroupKeys.sort((a, b) => {
        if (a === 'Unknown date') return 1
        if (b === 'Unknown date') return -1
        const dateA = new Date(a)
        const dateB = new Date(b)
        return sortOrder === 'desc' ? dateB - dateA : dateA - dateB
      })
    } else if (sortBy === 'topic') {
      sortedGroupKeys.sort((a, b) => {
        return sortOrder === 'desc' ? b.localeCompare(a) : a.localeCompare(b)
      })
    } else if (sortBy === 'correctness') {
      sortedGroupKeys.sort((a, b) => {
        if (sortOrder === 'desc') {
          return a === 'Correct' ? -1 : 1
        } else {
          return a === 'Incorrect' ? -1 : 1
        }
      })
    }

    const getQuestionText = (question) => {
      return question.question?.latex || 
             question.question?.text || 
             question.questionText ||
             (typeof question.question === 'string' ? question.question : 'No question text')
    }

    const getQuestionAttempts = (question) => {
      return question.attempts && question.attempts.length > 0 
        ? question.attempts 
        : question.allAttempts && question.allAttempts.length > 0
        ? question.allAttempts
        : [{
            answer: question.answer,
            feedback: question.feedback,
            studentAnswer: question.studentAnswer
          }]
    }

    const isQuestionCorrect = (question) => {
      const attempts = getQuestionAttempts(question)
      const lastAttempt = attempts[attempts.length - 1]
      return lastAttempt?.feedback?.isCorrect === true
    }

    const hasAttempts = (question) => {
      return (question.attempts && question.attempts.length > 0) || 
             (question.allAttempts && question.allAttempts.length > 0) ||
             (question.answer || question.feedback || question.studentAnswer)
    }

    return (
      <div className={`parent-desktop-view-work-layout ${selectedViewWorkQuestion ? 'question-selected' : ''}`}>
        <div className="parent-desktop-questions-menu">
          <div className="parent-desktop-questions-menu-header">
            <div className="parent-desktop-questions-title">
              {studentRosterName(selectedStudent)}'s Work
            </div>
            <div className="parent-desktop-questions-menu-controls">
              <div className="parent-desktop-sort-container">
                <button className="parent-desktop-sort-button" onClick={handleViewWorkSortClick} title="Sort options">
                  <SortIcon />
                </button>
                <button 
                  className="parent-desktop-sort-order-button" 
                  onClick={handleToggleViewWorkSortOrder}
                  title={sortOrder === 'asc' ? 'Toggle descending order' : 'Toggle ascending order'}
                >
                  {sortOrder === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                </button>
                {showViewWorkSortDropdown && (
                  <div className="parent-desktop-sort-dropdown">
                    <div className="dropdown-item" onClick={() => handleViewWorkSortSelect('date')}>Date</div>
                    <div className="dropdown-item" onClick={() => handleViewWorkSortSelect('topic')}>Topic</div>
                    <div className="dropdown-item" onClick={() => handleViewWorkSortSelect('correctness')}>Correct/Incorrect</div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="parent-desktop-questions-list-scroll">
            {sortedGroupKeys.filter(groupName => groupedQuestions[groupName].some(hasAttempts)).map(groupName => (
              <div key={groupName} className="parent-desktop-date-section">
                <div className="parent-desktop-date-header">{groupName}</div>
                {groupedQuestions[groupName].filter(hasAttempts).map(question => {
                  const questionText = getQuestionText(question)
                  const isCorrect = isQuestionCorrect(question)
                  const isSelected = selectedViewWorkQuestion?.id === question.id

                  return (
                    <div 
                      key={question.id} 
                      className={`parent-desktop-question-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelectViewWorkQuestion(question)}
                    >
                      <div className="parent-desktop-question-preview">
                        <MixedContentPreview content={questionText} fontSize="14px" />
                      </div>
                      {question.sourceLabel && (
                        <div className="parent-desktop-question-source-label">{question.sourceLabel}</div>
                      )}
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
        <div className="parent-desktop-answers-viewer">
          {selectedViewWorkQuestion && hasAttempts(selectedViewWorkQuestion) ? (
            <div className="parent-desktop-question-detail">
              <button 
                className="parent-desktop-mobile-back-to-questions"
                onClick={() => setSelectedViewWorkQuestion(null)}
              >
                ← Back to Questions
              </button>
              <div className="parent-desktop-detail-left-column">
                <div className="parent-desktop-question-text-section">
                  <div className="parent-desktop-detail-label">Question</div>
                  {selectedViewWorkQuestion.sourceLabel && (
                    <div className="parent-desktop-question-source-label">{selectedViewWorkQuestion.sourceLabel}</div>
                  )}
                  {selectedViewWorkQuestion.timestamp && (
                    <div className="parent-desktop-question-timestamp">
                      {selectedViewWorkQuestion.timestamp.toDate?.().toLocaleString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </div>
                  )}
                  <div className="parent-desktop-detail-content">
                    <MixedContentPreview 
                      content={getQuestionText(selectedViewWorkQuestion)} 
                      fontSize="18px"
                    />
                  </div>
                </div>

                {(() => {
                  const attempts = getQuestionAttempts(selectedViewWorkQuestion)

                  return (
                    <div className="parent-desktop-attempts-section">
                      <div className="parent-desktop-detail-label">
                        {attempts.length > 1 ? `All Attempts (${attempts.length})` : 'Attempt'}
                      </div>
                      <div className="parent-desktop-attempts-list">
                        {attempts.map((attempt, index) => {
                          const attemptAnswer = attempt.answer?.latex || 
                                                attempt.answer?.text ||
                                                attempt.studentAnswer?.latex ||
                                                attempt.studentAnswer?.text ||
                                                (typeof attempt.answer === 'string' ? attempt.answer : '') ||
                                                (typeof attempt.studentAnswer === 'string' ? attempt.studentAnswer : 'No answer')
                          const attemptCorrect = attempt.feedback?.isCorrect === true
                          const attemptFeedback = attempt.feedback?.feedback || attempt.feedback?.message || ''

                          return (
                            <div 
                              key={index} 
                              className="parent-desktop-attempt-card"
                            >
                              <div className="parent-desktop-attempt-layout">
                                <div className={`parent-desktop-attempt-status-icon ${attemptCorrect ? 'correct' : 'incorrect'}`}>
                                  {attemptCorrect ? <CheckCircleIcon /> : <BlockIcon />}
                                </div>
                                <div className="parent-desktop-attempt-main-content">
                                  <div className="parent-desktop-attempt-header">
                                    <span className="attempt-number">Attempt #{index + 1}</span>
                                  </div>

                                  <div className="parent-desktop-attempt-content-vertical">
                                    <div className="parent-desktop-attempt-answer">
                                      <div className="attempt-answer-label">Answer:</div>
                                      <div className="attempt-answer-content">
                                        <MixedContentPreview content={attemptAnswer} fontSize="16px" />
                                      </div>
                                    </div>

                                    {attemptFeedback && (
                                      <div className="parent-desktop-attempt-feedback">
                                        <div className="attempt-feedback-label">AI Feedback:</div>
                                        <div className="attempt-feedback-content">
                                          {attemptFeedback}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })()}
              </div>

              <div className="parent-desktop-detail-right-column">
                {selectedViewWorkQuestion.imageUrl && (
                  <div className="parent-desktop-question-image-container">
                    <img 
                      src={selectedViewWorkQuestion.imageUrl} 
                      alt="Question" 
                      className="parent-desktop-question-image"
                    />
                  </div>
                )}
                
                {selectedViewWorkQuestion.parentFeedback?.answerSummary && (
                  <div className="parent-desktop-summary-section">
                    <div className="parent-desktop-detail-label">Answer Summary</div>
                    <div className="parent-desktop-detail-content summary-text">
                      {selectedViewWorkQuestion.parentFeedback.answerSummary}
                    </div>
                    {selectedViewWorkQuestion.chatConversation && selectedViewWorkQuestion.chatConversation.length > 0 ? (
                      <button
                        className="parent-desktop-view-chat-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowChatConversationModal(true);
                        }}
                      >
                        View AI Helper Conversation
                      </button>
                    ) : (
                      <button
                        className="parent-desktop-view-chat-btn disabled"
                        disabled
                      >
                        Conversation Not Available
                      </button>
                    )}
                  </div>
                )}

                {desktopVideoRecommendations.length > 0 && (
                  <div className="parent-desktop-videos-section">
                    <div className="parent-desktop-detail-label">Recommended Videos</div>
                    <div className="parent-desktop-videos-grid">
                      {desktopVideoRecommendations.map((video) => (
                        <div
                          key={video.id}
                          className="parent-desktop-video-card"
                          onClick={() => window.open(video.url, '_blank', 'noopener,noreferrer')}
                        >
                          <div className="parent-desktop-video-thumbnail">
                            {video.thumbnail ? (
                              <img src={video.thumbnail} alt={video.title} />
                            ) : (
                              <div className="parent-desktop-video-play-icon">▶</div>
                            )}
                            <div className="parent-desktop-video-platform">
                              {video.url?.includes('youtube.com') || video.url?.includes('youtu.be') ? 'YouTube' :
                               video.url?.includes('khanacademy.org') ? 'Khan Academy' : 'Video'}
                            </div>
                          </div>
                          <div className="parent-desktop-video-title">{video.title}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="parent-desktop-placeholder-text">Select a question to view answers</p>
          )}
        </div>
      </div>
    )
  }

  // "Add a login later" for a credential-light student. The student keeps the
  // same profile id, so everything they have already done stays with them.
  const renderAddStudentLoginSection = () => {
    const firstName = selectedStudent?.firstName || 'This student'

    return (
      <div className="parent-desktop-settings-section">
        <div className="parent-desktop-settings-item">
          <div className="parent-desktop-settings-label">Add a Login</div>
        </div>
        <div className="parent-desktop-settings-disclaimer">
          {firstName} uses Quillnote from your account and has no login of their own.
          Add one when they are ready to sign in on their own device — every question,
          page, and XP already on this profile stays exactly where it is.
        </div>

        <div className="student-credentials-form">
          {!linkUseParentEmail && (
            <input
              type="email"
              className="student-input"
              placeholder="Student Email"
              value={linkFormData.email}
              onChange={(e) => setLinkFormData({ ...linkFormData, email: e.target.value })}
              disabled={linkingCredentials}
            />
          )}

          {linkUseParentEmail && (
            <input
              type="text"
              className="student-input"
              placeholder="Student Username to Sign In"
              value={linkFormData.username}
              onChange={(e) => setLinkFormData({ ...linkFormData, username: e.target.value })}
              disabled={linkingCredentials}
            />
          )}

          <div className="student-toggle-row">
            <label className="student-toggle-label" htmlFor="link-use-parent-email-toggle">
              Use my email instead
            </label>
            <label className="toggle-switch">
              <input
                id="link-use-parent-email-toggle"
                type="checkbox"
                checked={linkUseParentEmail}
                onChange={(e) => setLinkUseParentEmail(e.target.checked)}
                disabled={linkingCredentials}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="password-field-wrapper">
            <input
              type={showLinkPassword ? 'text' : 'password'}
              className="student-input"
              placeholder="Password (8 char, 1 num, 1 special char)"
              value={linkFormData.password}
              onChange={(e) => setLinkFormData({ ...linkFormData, password: e.target.value })}
              disabled={linkingCredentials}
            />
            <button
              type="button"
              className="password-toggle-modal"
              onClick={() => setShowLinkPassword(!showLinkPassword)}
              disabled={linkingCredentials}
            >
              {showLinkPassword ? 'hide' : 'show'}
            </button>
          </div>

          <button
            className="save-button"
            onClick={handleLinkStudentCredentials}
            disabled={linkingCredentials}
          >
            {linkingCredentials ? 'Adding login...' : 'Add Login'}
          </button>
        </div>
      </div>
    )
  }

  const renderDesktopSettingsContent = () => {
    const studentHasLogin = hasStudentLogin(selectedStudent)
    const firstName = selectedStudent?.firstName || 'This student'

    return (
      <div className="parent-desktop-settings-content">
        <div className="parent-desktop-settings-section">
          <div className="parent-desktop-settings-item">
            <div className="parent-desktop-settings-label">Student's first name</div>
            <div className="parent-desktop-settings-control">
              <input
                type="text"
                className="student-input"
                value={settingsFirstName}
                onChange={(event) => setSettingsFirstName(event.target.value)}
                disabled={savingSettingsFirstName}
                aria-label="Student's first name"
                style={{ fontSize: 16 }}
              />
              <button
                type="button"
                className="save-button parent-settings-action-btn"
                onClick={handleSaveLinkedFirstName}
                disabled={savingSettingsFirstName}
              >
                {savingSettingsFirstName ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
          <div className="parent-desktop-settings-disclaimer">
            You can add this later.
          </div>
        </div>
        {!studentHasLogin && renderAddStudentLoginSection()}

        <div className="parent-desktop-settings-section">
          <div className="parent-desktop-settings-item">
            <div className="parent-desktop-settings-label">Student View</div>
            <div className="parent-desktop-settings-control">
              <button
                className="save-button parent-settings-action-btn"
                onClick={() => handleEnterStudentView(selectedStudent)}
                disabled={enteringStudentView}
              >
                {enteringStudentView ? 'Opening...' : 'Open Student View'}
              </button>
            </div>
          </div>
          <div className="parent-desktop-settings-disclaimer">
            Hand your device to {firstName} with the full Study Helper open on their
            profile — everything they do is saved to their work and their XP.
            {studentHasLogin
              ? ' '
              : ` This is how ${firstName} uses Quillnote until you add a login for them. `}
            {parentPinSet
              ? 'Leaving Student View requires your parent PIN.'
              : 'You can set a PIN in Settings so leaving Student View requires it.'}
          </div>
        </div>
        <div className="parent-desktop-settings-section">
          <div className="parent-desktop-settings-item">
            <div className="parent-desktop-settings-label">AI Chat Helper</div>
            <div className="parent-desktop-settings-control">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={aiChatHelperEnabled}
                  onChange={handleToggleAiChatHelper}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
          <div className="parent-desktop-settings-disclaimer">
            Quillnote uses OpenAI for the chat helper. Quillnote does not share your child's personally identifiable information with the chat helper, and the chat helper will ignore any personally identifiable information that your student may attempt to share.<br /><br />OpenAI is an AI and can make mistakes. Please double-check AI responses for accuracy.
          </div>
        </div>
        <div className="parent-desktop-settings-section">
          <div className="parent-desktop-settings-item">
            <div className="parent-desktop-settings-label">Allow students to send feedback/issue reports</div>
            <div className="parent-desktop-settings-control">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={reportsEnabled}
                  onChange={() => handleToggleStudentReportSetting(
                    'reportsEnabled',
                    reportsEnabled,
                    setReportsEnabled,
                    'Feedback/issue reports enabled',
                    'Feedback/issue reports disabled'
                  )}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
          <div className="parent-desktop-settings-disclaimer">
            {firstName} can send Quillnote a bug report, feedback, or a feature request from Study Helper. Quillnote emails you and our team, and follows up with you only. We do not email {firstName}. This stays on until you turn it off. Reports are kept for 90 days.
          </div>
        </div>
        {reportsEnabled && (
          <div className="parent-desktop-settings-section">
            <div className="parent-desktop-settings-item">
              <div className="parent-desktop-settings-label">Feedback/Issue Screenshots</div>
              <div className="parent-desktop-settings-control">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={reportScreencapEnabled}
                    onChange={() => handleToggleStudentReportSetting(
                      'reportScreencapEnabled',
                      reportScreencapEnabled,
                      setReportScreencapEnabled,
                      'Feedback/issue screenshots enabled',
                      'Feedback/issue screenshots disabled'
                    )}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            <div className="parent-desktop-settings-disclaimer">
              When this is on, {firstName} can choose to include a picture of the page with a report. The report window is not included, and {firstName}'s name is hidden in the picture. Quillnote does not share these pictures or your student's personal information with third parties. Pictures are kept for 90 days.
            </div>
          </div>
        )}
        {studentHasLogin && (
          <div className="parent-desktop-settings-section">
            <div className="parent-desktop-settings-item">
              <div className="parent-desktop-settings-label">Password Reset</div>
              <div className="parent-desktop-settings-control">
                <button
                  className="save-button parent-settings-action-btn"
                  onClick={handleSendPasswordReset}
                  disabled={sendingPasswordReset}
                >
                  {sendingPasswordReset ? 'Sending...' : 'Send Reset Email'}
                </button>
              </div>
            </div>
            <div className="parent-desktop-settings-disclaimer">
              Sends a password reset link to the email address on file for this student. If no student email was provided, the link will be sent to your email instead.
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderDesktopManageWorkContent = () => {
    return (
      <div className="parent-desktop-manage-work-content">
        {renderManageWorkContent()}
      </div>
    )
  }

  const renderDesktopStudentView = () => {
    return (
      <div className="parent-desktop-main-content">
        <div className="parent-desktop-student-menu-bar">
          <div className="parent-desktop-breadcrumb">
            <button className="parent-desktop-back-button" onClick={handleBackToStudentsDesktop}>
              My Students
            </button>
            <span className="parent-desktop-breadcrumb-separator">›</span>
            <span className="parent-desktop-current-student">
              {studentRosterName(selectedStudent)}
            </span>
          </div>
          <div className="parent-desktop-menu-buttons">
            <button
              className={`parent-desktop-menu-item ${selectedStudentMenuItem === 'manage-work' ? 'selected' : ''}`}
              onClick={() => {
                setSelectedStudentMenuItem('manage-work')
                loadPages()
              }}
            >
              Manage Work
            </button>
            <button
              className={`parent-desktop-menu-item ${selectedStudentMenuItem === 'view-work' ? 'selected' : ''}`}
              onClick={() => setSelectedStudentMenuItem('view-work')}
            >
              View Work
            </button>
            <button
              className={`parent-desktop-menu-item ${selectedStudentMenuItem === 'explain' ? 'selected' : ''}`}
              onClick={() => setSelectedStudentMenuItem('explain')}
            >
              Explain
            </button>
            <button
              className={`parent-desktop-menu-item ${selectedStudentMenuItem === 'settings' ? 'selected' : ''}`}
              onClick={() => setSelectedStudentMenuItem('settings')}
            >
              Settings
            </button>
            <button
              className="parent-desktop-menu-item parent-desktop-student-view-btn"
              onClick={() => handleEnterStudentView(selectedStudent)}
              disabled={enteringStudentView}
              title={`Hand the device to ${selectedStudent.firstName || 'your student'}`}
            >
              {enteringStudentView ? 'Opening...' : 'Student View'}
            </button>
          </div>
        </div>

        {selectedStudentMenuItem === 'manage-work' && renderDesktopManageWorkContent()}
        {selectedStudentMenuItem === 'view-work' && renderDesktopQuestionCards()}
        {selectedStudentMenuItem === 'explain' && selectedStudent && (
          <ParentExplainPanel
            studentId={selectedStudent.id}
            studentName={selectedStudent.firstName || 'This student'}
          />
        )}
        {selectedStudentMenuItem === 'settings' && renderDesktopSettingsContent()}
      </div>
    )
  }

  const renderParentEmailSettingsContent = () => {
    return (
      <div className="parent-desktop-main-content">
        <div className="parent-desktop-student-menu-bar">
          <span className="parent-desktop-current-student">Settings</span>
          <div className="parent-desktop-menu-buttons">
            <button
              className={`parent-desktop-menu-item ${selectedSettingsTab === 'notifications' ? 'selected' : ''}`}
              onClick={() => setSelectedSettingsTab('notifications')}
            >
              Notifications
            </button>
            <button
              className={`parent-desktop-menu-item ${selectedSettingsTab === 'security' ? 'selected' : ''}`}
              onClick={() => setSelectedSettingsTab('security')}
            >
              Security
            </button>
            <button
              className={`parent-desktop-menu-item ${selectedSettingsTab === 'tutorial' ? 'selected' : ''}`}
              onClick={() => setSelectedSettingsTab('tutorial')}
            >
              Tutorial
            </button>
          </div>
        </div>

        {selectedSettingsTab === 'notifications' && (
          <div className="parent-desktop-students-browser parent-desktop-settings-browser">
            <div className="parent-desktop-settings-content">
              <div className="parent-desktop-settings-section">
                <div className="parent-desktop-settings-item">
                  <div className="parent-desktop-settings-label">Welcome Email</div>
                  <div className="parent-desktop-settings-control">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={emailWelcomeEnabled}
                        onChange={() => handleEmailPrefToggle('welcomeEmail', emailWelcomeEnabled, setEmailWelcomeEnabled)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
                <div className="parent-desktop-settings-disclaimer">
                  Receive a welcome email when your Quillnote parent account is created. Turning this off will suppress the welcome email for newly created accounts.
                </div>
              </div>

              <div className="parent-desktop-settings-section">
                <div className="parent-desktop-settings-item">
                  <div className="parent-desktop-settings-label">Daily Activity Summary</div>
                  <div className="parent-desktop-settings-control">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={emailDailySummaryEnabled}
                        onChange={() => handleEmailPrefToggle('dailySummary', emailDailySummaryEnabled, setEmailDailySummaryEnabled)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
                <div className="parent-desktop-settings-disclaimer">
                  Receive a daily email summarising check-answer questions and Explain activity from that day. Emails are only sent on days when there is activity.
                </div>
              </div>

              <div className="parent-desktop-settings-section">
                <div className="parent-desktop-settings-item">
                  <div className="parent-desktop-settings-label">Weekly Digest</div>
                  <div className="parent-desktop-settings-control">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={emailWeeklyDigestEnabled}
                        onChange={() => handleEmailPrefToggle('weeklyDigest', emailWeeklyDigestEnabled, setEmailWeeklyDigestEnabled)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
                <div className="parent-desktop-settings-disclaimer">
                  Receive a weekly summary every Sunday at 4pm covering all of your children's activity for the week — check-answer results, Explain sessions, and insights.
                </div>
              </div>

              <div className="parent-desktop-settings-section">
                <div className="parent-desktop-settings-item">
                  <div className="parent-desktop-settings-label">App Update Emails</div>
                  <div className="parent-desktop-settings-control">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={emailVersionUpdatesEnabled}
                        onChange={() => handleEmailPrefToggle('versionUpdates', emailVersionUpdatesEnabled, setEmailVersionUpdatesEnabled)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
                <div className="parent-desktop-settings-disclaimer">
                  Receive an email whenever a new version of Quillnote is released, highlighting new features, improvements, and fixes.
                </div>
              </div>

            </div>
          </div>
        )}

        {selectedSettingsTab === 'security' && (
          <div className="parent-desktop-students-browser parent-desktop-settings-browser">
            <div className="parent-desktop-settings-content">
              <div className="parent-desktop-settings-section">
                <div className="parent-desktop-settings-item">
                  <div className="parent-desktop-settings-label">Student View PIN</div>
                  <div className="parent-desktop-settings-control">
                    {parentPinSet ? (
                      <span className="parent-desktop-pin-state parent-desktop-pin-state--on">
                        PIN set
                      </span>
                    ) : (
                      <button
                        className="save-button parent-settings-action-btn"
                        onClick={() => openPinModal('set')}
                      >
                        Set a PIN
                      </button>
                    )}
                  </div>
                </div>
                <div className="parent-desktop-settings-disclaimer">
                  One PIN for your whole account. With a PIN set, leaving Student View
                  asks for it, so a child cannot get back to your dashboard, your other
                  students, or these settings on their own. Your PIN is stored only as a
                  salted hash and is checked on our servers — never on this device.
                </div>
                {parentPinSet && (
                  <div className="parent-desktop-pin-actions">
                    <button
                      className="save-button parent-settings-action-btn"
                      onClick={() => openPinModal('change')}
                    >
                      Change PIN
                    </button>
                    <button
                      className="close-button parent-settings-action-btn"
                      onClick={() => openPinModal('remove')}
                    >
                      Remove PIN
                    </button>
                    <button
                      className="close-button parent-settings-action-btn"
                      onClick={handleSendPinResetCode}
                      disabled={sendingResetCode}
                    >
                      {sendingResetCode ? 'Sending code...' : 'Forgot PIN'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {selectedSettingsTab === 'tutorial' && (
          <div className="parent-desktop-students-browser parent-desktop-settings-browser">
            <div className="parent-desktop-settings-content">
              <div className="parent-desktop-settings-section">
                <div className="parent-desktop-settings-item">
                  <div className="parent-desktop-settings-label">Dashboard Tutorial</div>
                  <div className="parent-desktop-settings-control">
                    <button
                      className="save-button parent-settings-action-btn"
                      onClick={handleRestartTutorial}
                    >
                      Tutorial
                    </button>
                  </div>
                </div>
                <div className="parent-desktop-settings-disclaimer">
                  Replay the guided tour of your parent dashboard using an example student.
                </div>
              </div>

              <div className="parent-desktop-settings-section">
                <div className="parent-desktop-settings-item">
                  <div className="parent-desktop-settings-label">Study Helper Demo</div>
                  <div className="parent-desktop-settings-control">
                    <button
                      className="save-button parent-settings-action-btn"
                      onClick={handleOpenDemo}
                    >
                      Replay Demo
                    </button>
                  </div>
                </div>
                <div className="parent-desktop-settings-disclaimer">
                  Try the interactive Explain &amp; Check Answer demo again with your child. Nothing in the demo is saved to a real student.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderDesktopContent = () => {
    if (selectedMenuItem === 'manage-tiers') {
      return (
        <ManageTiers
          loading={loading}
          rows={students.map((student) => ({
            id: student.id,
            displayName: studentRosterName(student),
            summary: billingById[student.id] || null
          }))}
          summaryFailed={billingSummaryError}
          onDismissSummaryError={() => setBillingSummaryError(false)}
          onOpen={(studentId) => {
            const student = students.find((item) => item.id === studentId)
            if (student) openStudentTier(student)
          }}
        />
      )
    }

    if (selectedMenuItem === 'my-students' && selectedStudent) {
      return renderDesktopStudentView()
    }

    if (selectedMenuItem === 'my-students') {
      return (
        <div className="parent-desktop-main-content">
          {billingSummaryError && (
            <div className="parent-billing-summary-error-slot">
              <BillingSummaryNotice onDismiss={() => setBillingSummaryError(false)} />
            </div>
          )}
          {renderDesktopStudentsGrid()}
        </div>
      )
    }

    if (selectedMenuItem === 'settings') {
      return renderParentEmailSettingsContent()
    }

    return null
  }

  const renderDesktopSideMenu = () => (
    <div className="parent-desktop-side-menu">
      <div className="parent-desktop-menu-header">
        <div className="parent-desktop-header-line-one">
          <img src='/logo.svg' alt='Quillnote Logo' className='parent-desktop-logo-icon' />
          <span className="parent-desktop-app-name brand-text">Quillnote</span>
        </div>
        <div className="parent-desktop-header-line-two">
          <BetaVersionBadge size="sm" />
          <span className="parent-desktop-role-label">Parent</span>
        </div>
      </div>

      <div className="parent-desktop-menu-items">
        <button
          className={`parent-desktop-side-menu-item ${selectedMenuItem === 'my-students' ? 'selected' : ''}`}
          onClick={() => {
            setSelectedMenuItem('my-students')
            setSelectedStudent(null)
            setStudyQuestions([])
          }}
        >
          My Students
        </button>
        <button
          className="parent-desktop-side-menu-item"
          onClick={() => setShowCreateStudentModal(true)}
        >
          Add Student
        </button>
        <button
          className={`parent-desktop-side-menu-item ${selectedMenuItem === 'manage-tiers' ? 'selected' : ''}`}
          onClick={() => {
            setSelectedMenuItem('manage-tiers')
            setSelectedStudent(null)
            setStudyQuestions([])
          }}
        >
          Manage Tiers
        </button>
        {new Date() < COMPETITION_END && (
          <button
            className="parent-desktop-side-menu-item comp-menu-item"
            onClick={() => setShowCompetitionModal(true)}
          >
            🏆 XP Competition
          </button>
        )}
        <button
          className={`parent-desktop-side-menu-item ${selectedMenuItem === 'settings' ? 'selected' : ''}`}
          onClick={() => {
            setSelectedMenuItem('settings')
            setSelectedStudent(null)
            setStudyQuestions([])
          }}
        >
          Settings
        </button>
        <button
          className="parent-desktop-side-menu-item"
          onClick={handleRestartTutorial}
        >
          Tutorial
        </button>
        <button
          className="parent-desktop-side-menu-item"
          onClick={() => window.open('https://docs.google.com/forms/d/e/1FAIpQLSc-_uUUJaaa6A7Ut2k58O5QwxVRD9Lcct2uRc9tyysq2q5LGw/viewform?usp=sharing&ouid=113871909585680355100', '_blank')}
        >
          Report Feedback/Issue
        </button>
      </div>

      <div className="parent-desktop-menu-bottom">
        <div className="parent-desktop-theme-toggle-wrapper">
          <ThemeToggleButton />
        </div>
        <button
          className="parent-desktop-side-menu-item logout-item"
          onClick={handleLogout}
        >
          Logout
        </button>
      </div>
    </div>
  )

  const renderMobileTopHeader = () => (
    <div className="parent-mobile-top-header">
      <div className="parent-mobile-header-brand">
        <img src='/logo.svg' alt='Quillnote Logo' className='parent-mobile-logo-icon' />
        <span className="parent-mobile-header-app-name brand-text">Quillnote</span>
      </div>
      <div className="parent-mobile-header-actions">
        <BetaVersionBadge size="sm" />
        <ThemeToggleButton />
        <div className="parent-mobile-more-wrap">
          <button
            className="parent-mobile-more-btn"
            onClick={() => setShowMobileMoreMenu(v => !v)}
            aria-label="More options"
          >
            <MoreVertIcon style={{ fontSize: 22 }} />
          </button>
          {showMobileMoreMenu && (
            <>
              <div
                className="parent-mobile-more-backdrop"
                onClick={() => setShowMobileMoreMenu(false)}
              />
              <div className="parent-mobile-more-dropdown">
                <button
                  className={`parent-mobile-more-item ${selectedMenuItem === 'manage-tiers' ? 'parent-mobile-more-item-selected' : ''}`}
                  onClick={() => {
                    setSelectedMenuItem('manage-tiers')
                    setSelectedStudent(null)
                    setStudyQuestions([])
                    setShowMobileMoreMenu(false)
                  }}
                >
                  Manage Tiers
                </button>
                {new Date() < COMPETITION_END && (
                  <button
                    className="parent-mobile-more-item"
                    onClick={() => {
                      setShowCompetitionModal(true)
                      setShowMobileMoreMenu(false)
                    }}
                  >
                    🏆 XP Competition
                  </button>
                )}
                <button
                  className="parent-mobile-more-item"
                  onClick={() => {
                    window.open('https://docs.google.com/forms/d/e/1FAIpQLSc-_uUUJaaa6A7Ut2k58O5QwxVRD9Lcct2uRc9tyysq2q5LGw/viewform?usp=sharing&ouid=113871909585680355100', '_blank')
                    setShowMobileMoreMenu(false)
                  }}
                >
                  Report Feedback / Issue
                </button>
                <button
                  className="parent-mobile-more-item"
                  onClick={() => { handleRestartTutorial(); setShowMobileMoreMenu(false) }}
                >
                  Tutorial
                </button>
                <button
                  className="parent-mobile-more-item parent-mobile-more-item-danger"
                  onClick={() => { handleLogout(); setShowMobileMoreMenu(false) }}
                >
                  Logout
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )

  const renderMobileBottomNav = () => (
    <div className="parent-mobile-bottom-nav">
      <button
        className={`parent-mobile-tab-btn ${selectedMenuItem === 'my-students' ? 'active' : ''}`}
        onClick={() => {
          setSelectedMenuItem('my-students')
          setSelectedStudent(null)
          setStudyQuestions([])
        }}
      >
        <PeopleIcon style={{ fontSize: 22 }} />
        <span>My Students</span>
      </button>
      <button
        className={`parent-mobile-tab-btn ${selectedMenuItem === 'settings' ? 'active' : ''}`}
        onClick={() => {
          setSelectedMenuItem('settings')
          setSelectedStudent(null)
          setStudyQuestions([])
        }}
      >
        <SettingsIcon style={{ fontSize: 22 }} />
        <span>Settings</span>
      </button>
    </div>
  )

  const renderDesktopLayout = () => {
    if (isMobile) {
      return (
        <div className="parent-dashboard-desktop dynamic-bg">
          {renderMobileTopHeader()}
          <div className="parent-mobile-content-area">
            {renderDesktopContent()}
          </div>
          {renderMobileBottomNav()}
        </div>
      )
    }

    return (
      <div className="parent-dashboard-desktop dynamic-bg">
        {renderDesktopSideMenu()}
        {renderDesktopContent()}
      </div>
    )
  }

  // The PIN keypad, its Settings flows, and the emailed reset all render from
  // one place so Student View and the dashboard cannot drift apart.
  const renderPinModal = () => {
    if (!pinModalMode) return null

    return (
      <ParentPinModal
        mode={pinModalMode}
        busy={pinBusy}
        error={pinError}
        notice={pinNotice}
        onSubmit={handlePinSubmit}
        onCancel={closePinModal}
        onForgotPin={handleSendPinResetCode}
        onSendResetCode={pinModalMode === 'reset' ? handleSendPinResetCode : null}
        sendingResetCode={sendingResetCode}
      />
    )
  }

  if (studentViewResolving) {
    return (
      <div className="parent-dashboard-desktop dynamic-bg">
        <div className="parent-desktop-loading">Loading...</div>
      </div>
    )
  }

  // Student View replaces the parent shell outright rather than hiding parts of
  // it: while it is active there is no My Students list, no Add Student, no
  // parent Settings, no other child, and no student settings rendered at all.
  // Nothing about the lockdown depends on a child not finding a button.
  if (studentViewActive) {
    return (
      <>
        <StudyRoom
          studentView
          actingStudentId={studentViewStudent.id}
          actingStudentProfile={studentViewStudent}
          onExitStudentView={handleRequestExitStudentView}
        />

        {showPinOffer && (
          <div className="modal-overlay">
            <div className="parent-code-modal">
              <h2>Protect exit with a PIN?</h2>
              <p>
                Set a 4–6 digit PIN and {studentViewStudent.firstName || 'your student'} will
                need it to leave Student View. You can add or change it later in Settings.
              </p>
              <div className="modal-actions">
                <button
                  className="save-button"
                  onClick={() => {
                    setShowPinOffer(false)
                    openPinModal('set')
                  }}
                >
                  Set a PIN
                </button>
                <button
                  className="close-button"
                  onClick={() => setShowPinOffer(false)}
                >
                  Not now
                </button>
              </div>
            </div>
          </div>
        )}

        {showLeaveStudentViewConfirm && (
          <div className="modal-overlay">
            <div className="parent-code-modal">
              <h2>Leave Student View?</h2>
              <p>
                You'll go back to your parent dashboard.
                {' '}{studentViewStudent.firstName || 'Your student'}'s work is already saved.
              </p>
              <div className="modal-actions">
                <button
                  className="save-button"
                  onClick={handleLeaveStudentViewWithoutPin}
                  disabled={leavingStudentView}
                >
                  {leavingStudentView ? 'Leaving...' : 'Leave Student View'}
                </button>
                <button
                  className="close-button"
                  onClick={() => setShowLeaveStudentViewConfirm(false)}
                  disabled={leavingStudentView}
                >
                  Keep working
                </button>
              </div>
            </div>
          </div>
        )}

        {renderPinModal()}
      </>
    )
  }

    return (
      <>
        {renderDesktopLayout()}

        {renderPinModal()}

        {showNameModal && (
          <ParentNameModal onComplete={handleNameModalComplete} />
        )}

        {showInviteClaim && (
          <div className="modal-overlay">
            <div className="parent-code-modal create-student-modal" role="dialog" aria-modal="true">
              <ParentInviteLinkForm
                token={inviteClaimToken}
                onLinked={async () => {
                  clearParentInviteToken()
                  setInviteClaimToken('')
                  await refreshStudents()
                }}
                onUnavailable={() => {
                  clearParentInviteToken()
                  setInviteClaimToken('')
                }}
              />
            </div>
          </div>
        )}

        {showOnboardingChoice && (
          <OnboardingChoiceModal
            busy={resolvingOnboardingChoice}
            onAddStudent={() => handleOnboardingChoiceSelect('add_student')}
            onSeeDemo={() => handleOnboardingChoiceSelect('demo')}
            onDismiss={() => handleOnboardingChoiceSelect('dismissed')}
          />
        )}

        {showTutorial && !showNameModal && !showInviteClaim && !showOnboardingChoice && !showDemo && !showCreateStudentModal && (
          <ParentTutorial
            currentUser={currentUser}
            onComplete={handleTutorialComplete}
          />
        )}

        {showChecklist && !showTutorial && !showNameModal && !showInviteClaim && !showOnboardingChoice && !showDemo && !showCreateStudentModal && (
          <ParentChecklist
            currentUser={currentUser}
            students={students}
            demoCompleted={userProfile?.demoTutorialCompleted === true}
            autoOpen={checklistAutoOpen}
            onAddStudent={handleAddStudent}
            onOpenDemo={handleOpenDemo}
            onOpenChange={setChecklistPanelOpen}
          />
        )}

        {showDemo && (
          <StudyHelperDemo
            onClose={handleCloseDemo}
            onComplete={handleDemoComplete}
          />
        )}

        {showCreateStudentModal && (
          <div className="modal-overlay" onClick={(e) => {
            if (e.target.className === 'modal-overlay') {
              handleCloseCreateStudentModal()
            }
          }}>
            <div className="parent-code-modal create-student-modal">
              <h2>Add Student</h2>
              <p>
                {studentViewOnly
                  ? "Add your student's first name. You'll open their work from your own account — no login for them to remember."
                  : "Create an account for your student. They'll use this to log in."}
              </p>

              <input
                type="text"
                className="student-input"
                placeholder="First Name"
                value={studentFormData.firstName}
                onChange={(e) => setStudentFormData({...studentFormData, firstName: e.target.value})}
                disabled={creatingStudent}
              />

              <div className="student-toggle-row">
                <label className="student-toggle-label" htmlFor="student-view-only-toggle">
                  This student will only use Quillnote from my account
                </label>
                <label className="toggle-switch">
                  <input
                    id="student-view-only-toggle"
                    type="checkbox"
                    checked={studentViewOnly}
                    onChange={(e) => handleToggleStudentViewOnly(e.target.checked)}
                    disabled={creatingStudent}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <p className="student-toggle-hint">
                {studentViewOnly
                  ? 'No email, username, or password needed. You can add a login later from their settings.'
                  : "Turn this on for a child who shares your device and won't sign in on their own."}
              </p>

              {!studentViewOnly && (
                <>
                  {!useParentEmail && (
                    <input
                      type="email"
                      className="student-input"
                      placeholder="Student Email"
                      value={studentFormData.email}
                      onChange={(e) => setStudentFormData({...studentFormData, email: e.target.value})}
                      disabled={creatingStudent}
                    />
                  )}

                  {useParentEmail && (
                    <input
                      type="text"
                      className="student-input"
                      placeholder="Student Username to Sign In"
                      value={studentFormData.username}
                      onChange={(e) => setStudentFormData({...studentFormData, username: e.target.value})}
                      disabled={creatingStudent}
                    />
                  )}

                  <div className="student-toggle-row">
                    <label className="student-toggle-label" htmlFor="use-parent-email-toggle">
                      Use my email instead
                    </label>
                    <label className="toggle-switch">
                      <input
                        id="use-parent-email-toggle"
                        type="checkbox"
                        checked={useParentEmail}
                        onChange={(e) => setUseParentEmail(e.target.checked)}
                        disabled={creatingStudent}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="password-field-wrapper">
                    <input
                      type={showPasswordInModal ? 'text' : 'password'}
                      className="student-input"
                      placeholder="Password (8 char, 1 num, 1 special char)"
                      value={studentFormData.password}
                      onChange={(e) => setStudentFormData({...studentFormData, password: e.target.value})}
                      disabled={creatingStudent}
                    />
                    <button
                      type="button"
                      className="password-toggle-modal"
                      onClick={() => setShowPasswordInModal(!showPasswordInModal)}
                      disabled={creatingStudent}
                    >
                      {showPasswordInModal ? 'hide' : 'show'}
                    </button>
                  </div>
                </>
              )}

              {new Date() < COMPETITION_END && (
                <label className="create-student-competition-opt">
                  <input
                    type="checkbox"
                    checked={enrollInCompetition}
                    onChange={(e) => setEnrollInCompetition(e.target.checked)}
                    disabled={creatingStudent}
                  />
                  <span>Enroll in XP Competition 🏆</span>
                </label>
              )}

              <div className="modal-actions">
                <button 
                  className="save-button" 
                  onClick={handleCreateStudent}
                  disabled={creatingStudent}
                >
                  {creatingStudent
                    ? (studentViewOnly ? 'Adding...' : 'Creating...')
                    : (studentViewOnly ? 'Add Student' : 'Create Account')}
                </button>
                <button 
                  className="close-button" 
                  onClick={handleCloseCreateStudentModal}
                  disabled={creatingStudent}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {selectedQuestion && (
          <QuestionDetailModal
            question={selectedQuestion}
            onClose={() => setSelectedQuestion(null)}
            selectedStudent={selectedStudent}
          />
        )}

        {showChatConversationModal && selectedViewWorkQuestion?.chatConversation && (
          <div className="chat-conversation-modal-overlay" onClick={() => setShowChatConversationModal(false)}>
            <div className="chat-conversation-modal" onClick={(e) => e.stopPropagation()}>
              <div className="chat-conversation-modal-header">
                <h2>AI Helper Conversation</h2>
                <button className="chat-conversation-modal-close" onClick={() => setShowChatConversationModal(false)}>
                  ×
                </button>
              </div>
              <div className="chat-conversation-modal-messages">
                {selectedViewWorkQuestion.chatConversation.map((message, index) => (
                  <div 
                    key={index} 
                    className={`chat-conversation-message ${message.role === 'user' ? 'user-message' : 'assistant-message'}`}
                  >
                    <div className="chat-conversation-message-role">
                      {message.role === 'user' ? 'Student' : 'AI Tutor'}
                    </div>
                    <div className="chat-conversation-message-content">
                      {message.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {showPageImageCrop && pageImageSrc && (
          <ImageCrop
            key={pageImageSrc}
            imageSrc={pageImageSrc}
            onCropComplete={handlePageImageCropComplete}
            onCancel={() => {
              setShowPageImageCrop(false)
              setPageImageSrc(null)
            }}
          />
        )}

        {showCompetitionModal && !tierTarget && !showNameModal && !showInviteClaim && !showOnboardingChoice && !showDemo && !showCreateStudentModal && !checklistPanelOpen && (
          <CompetitionEnrollModal
            students={students.filter(s => isStudentRole(s.role))}
            enrolledIds={competitionEnrolledIds}
            onEnroll={handleEnrollStudents}
            onDismiss={handleDismissCompetitionModal}
          />
        )}

        {tierTarget && (
          <ParentTierModal
            summary={tierTarget.summary}
            displayName={studentRosterName(tierTarget.student)}
            onClose={() => setTierTarget(null)}
          />
        )}

        {showPageNumberModal && (
          <div className="modal-overlay" onClick={(e) => {
            if (e.target.className === 'modal-overlay') {
              setShowPageNumberModal(false)
              setPageNumber('')
            }
          }}>
            <div className="parent-code-modal">
              <h2>Add Page</h2>
              <p>Enter the page number to add.</p>
              <input
                type="number"
                className="student-input"
                placeholder="Page Number"
                value={pageNumber}
                onChange={(e) => setPageNumber(e.target.value)}
                min="1"
              />
              <div className="modal-actions">
                <button className="save-button" onClick={handleConfirmPageNumber}>
                  Continue
                </button>
                <button className="close-button" onClick={() => {
                  setShowPageNumberModal(false)
                  setPageNumber('')
                }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    )
}

export default ParentDashboard